import React, { useState, useEffect, useMemo, useRef, useCallback, useContext } from 'react';
import { TabContext } from '../components/TabbedLayout';
import { createPortal } from 'react-dom';
import { FiTruck, FiUpload, FiFileText, FiPackage, FiBarChart2, FiCheck } from "react-icons/fi";
import { MdDelete } from "react-icons/md";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Slide } from 'react-toastify';
import Swal from 'sweetalert2';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import axios from 'axios';
import withAuth from '../common/auth';
import AddItemModal from '../components/AddItemModal';
import AddSupplierModal from '../components/AddSupplierModal';
import * as XLSX from 'xlsx';
import {
  BASE_URL,
  GET_ITEM_MASTER,
  GET_SUPPLIER_MASTER,
  GET_BASE_DATA,
  POST_SUPPLIER_MAPPING,
  POST_PURCHASE_TRANSACTION,
  GET_PURCHASE_ALL_DATA,
  AI_EXTRACT_BASE_URL,
  EXTRACT_PURCHASE_BILL,
  AI_EXTRACT_CLIENT_ID,
  AI_EXTRACT_SERVICE_KEY,
  POST_ITEM_MASTER,
} from '../utlilities/config';
import { getWebUserInfo, initializeWebUserInfo } from '../utlilities/webConfig';

// Enhanced Spinner Component
const Spinner = () => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-600 font-medium">Loading...</p>
    </div>
  </div>
);

const AI_ANALYSIS_TASKS = [
  { label: 'Reading purchase bill PDF', icon: FiFileText },
  { label: 'Extracting invoice & supplier details', icon: FiFileText },
  { label: 'Identifying products & batches', icon: FiPackage },
  { label: 'Parsing rates, GST & quantities', icon: FiBarChart2 },
  { label: 'Matching with your item master', icon: FiCheck },
];

const AI_STATUS_MESSAGES = [
  'Analyzing purchase bill',
  'Extracting invoice details',
  'Identifying products & batches',
  'Parsing rates and GST',
  'Matching item master',
];

const AiRobotIcon = ({ className = 'w-7 h-7', color = 'currentColor' }) => (
  <svg className={className} viewBox="0 0 64 64" fill="none" aria-hidden="true">
    <rect x="14" y="22" width="36" height="30" rx="8" fill={color} />
    <rect x="20" y="30" width="8" height="8" rx="2" fill="white" opacity="0.95" />
    <rect x="36" y="30" width="8" height="8" rx="2" fill="white" opacity="0.95" />
    <rect x="27" y="42" width="10" height="3" rx="1.5" fill="white" opacity="0.85" />
    <path d="M32 22V14" stroke={color} strokeWidth="4" strokeLinecap="round" />
    <circle cx="32" cy="11" r="4" fill={color} />
    <path d="M14 34H8" stroke={color} strokeWidth="4" strokeLinecap="round" />
    <path d="M50 34H56" stroke={color} strokeWidth="4" strokeLinecap="round" />
  </svg>
);

const AiAnalysingLoader = () => {
  const [taskIndex, setTaskIndex] = useState(0);
  const [typedChars, setTypedChars] = useState(0);

  const statusMessage = AI_STATUS_MESSAGES[taskIndex];
  const showCursor = typedChars < statusMessage.length;

  useEffect(() => {
    setTypedChars(0);
    const typingInterval = setInterval(() => {
      setTypedChars((prev) => {
        if (prev >= statusMessage.length) {
          clearInterval(typingInterval);
          return prev;
        }
        return prev + 1;
      });
    }, 45);
    return () => clearInterval(typingInterval);
  }, [statusMessage]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTaskIndex((prev) => (prev + 1) % AI_ANALYSIS_TASKS.length);
    }, 3200);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/45 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-2xl overflow-hidden bg-white shadow-2xl border border-slate-200">
        <div className="relative px-5 py-4 bg-gradient-to-r from-sky-500 via-blue-600 to-violet-600 text-white">
          <div className="flex items-start gap-3 pr-8">
            <div className="relative shrink-0">
              <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30 shadow-inner">
                <AiRobotIcon className="w-7 h-7" color="#ffffff" />
              </div>
              <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-amber-400 border-2 border-violet-600 animate-pulse" />
            </div>
            <div className="min-w-0 pt-0.5">
              <p className="text-base font-semibold leading-tight">Purchase Bill Extraction</p>
              <p className="text-xs text-blue-100 mt-0.5">AI-Powered Invoice Analysis</p>
            </div>
          </div>
        </div>

        <div className="px-6 py-7 flex flex-col items-center">
          <div className="relative w-32 h-32 mb-5 flex items-center justify-center">
            <div className="absolute inset-0 animate-spin" style={{ animationDuration: '2.2s' }}>
              <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r="52" fill="none" stroke="#e2e8f0" strokeWidth="6" />
                <circle
                  cx="60"
                  cy="60"
                  r="52"
                  fill="none"
                  stroke="url(#aiRingGradient)"
                  strokeWidth="6"
                  strokeLinecap="round"
                  strokeDasharray="90 237"
                />
                <defs>
                  <linearGradient id="aiRingGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#38bdf8" />
                    <stop offset="50%" stopColor="#6366f1" />
                    <stop offset="100%" stopColor="#a855f7" />
                  </linearGradient>
                </defs>
              </svg>
            </div>

            <div className="relative z-10 w-16 h-16 rounded-2xl bg-slate-50 border border-slate-200 shadow-sm flex items-center justify-center">
              <AiRobotIcon className="w-10 h-10" color="#475569" />
            </div>
          </div>

          <p className="text-sm font-medium text-slate-800 min-h-[22px] text-center">
            {statusMessage.slice(0, typedChars)}
            <span className={`inline-block w-0.5 h-4 ml-0.5 align-middle bg-violet-500 ${showCursor ? 'animate-pulse' : 'opacity-0'}`} />
          </p>
          <p className="text-xs text-slate-400 mt-2 mb-5">This may take 15–30 seconds</p>

          <div className="w-full space-y-2.5">
            {AI_ANALYSIS_TASKS.map((task, index) => {
              const Icon = task.icon;
              const isDone = index < taskIndex;
              const isActive = index === taskIndex;

              return (
                <div
                  key={task.label}
                  className={`flex items-center gap-3 rounded-lg px-3 py-2 transition-colors ${
                    isActive ? 'bg-violet-50 border border-violet-100' : 'bg-transparent'
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                      isDone
                        ? 'bg-emerald-100 text-emerald-600'
                        : isActive
                          ? 'bg-violet-100 text-violet-600'
                          : 'bg-slate-100 text-slate-400'
                    }`}
                  >
                    {isDone ? <FiCheck className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  </div>
                  <span
                    className={`text-sm ${
                      isDone
                        ? 'text-emerald-700'
                        : isActive
                          ? 'text-violet-700 font-medium'
                          : 'text-slate-400'
                    }`}
                  >
                    {task.label}
                  </span>
                  {isActive && (
                    <span className="ml-auto flex gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-bounce" />
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: '0.15s' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-bounce" style={{ animationDelay: '0.3s' }} />
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '34px', // reduce overall control height
    height: '34px',    // explicitly set height
    backgroundColor: '#f9fafb',
    borderColor: state.isFocused ? '#6366f1' : '#b5b6b8ff',
    boxShadow: state.isFocused ? '0 0 0 1px #6366f1' : 'none',
    borderRadius: '0.5rem',
    '&:hover': {
      borderColor: '#6366f1',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '2px 8px', // reduce internal padding for smaller height
    height: '34px',
  }),
  input: (provided) => ({
    ...provided,
    margin: '0px',
    padding: '0px',
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: '34px',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#92c3fcff'
      : state.isFocused
        ? '#92c3fcff'
        : '#ffffff',
    color: state.isSelected ? '#000000ff' : state.isFocused ? '#ffffff' : '#000000',
    fontWeight: '500',
    padding: '6px 8px',
    '&:hover': {
      backgroundColor: '#92c3fcff',
      color: '#000000ff',
    },
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 999,
    borderRadius: '0.5rem',
    overflow: 'hidden',
    boxShadow:
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};

const normalizeProductData = (items = []) =>
  items.map((item) => {
    const itemNo = item.Item_No ?? item.ItemNo ?? item.id ?? null;
    const itemName = item.Item_Name ?? item.ItemName ?? '';
    const packageValue = item.package ?? item.Package ?? '';
    const unitValue = item.unit ?? item.Unit ?? '';

    return {
      ...item,
      id: item.id ?? itemNo,
      Item_No: itemNo,
      Item_Name: itemName,
      ItemName: item.ItemName ?? itemName,
      package: packageValue,
      Package: item.Package ?? packageValue,
      unit: unitValue,
      Unit: item.Unit ?? unitValue,
      TaxCode: item.TaxCode ?? item.taxCode ?? '',
      Rate: item.Rate ?? item.rate ?? 0,
      MRP: item.MRP ?? item.mrp ?? 0,
      HSNCode: item.HSNCode ?? item.hsnCode ?? item.HSN_Code ?? item.hsn_Code ?? '',
    };
  });

/** Trim, collapse spaces, NFC — for matching Excel / API strings to supplier map and item master */
const normalizeNameForMatch = (value) => {
  if (value === null || value === undefined) return '';
  const s = value.toString().trim().replace(/\s+/g, ' ');
  try {
    return s.normalize('NFC').toLowerCase();
  } catch {
    return s.toLowerCase();
  }
};

/** Excel HSN cells often paste as "30042099       30042099        0.00" — keep first whitespace-delimited token */
const parseHsnCodeFromCell = (raw) => {
  if (raw === null || raw === undefined) return '';
  const s = raw.toString().trim();
  if (!s) return '';
  const first = s.split(/\s+/)[0];
  return first || '';
};

/** Prefer Excel displayed text (e.g. "01-07-2027") over raw serial — avoids US mm/dd misreads */
const getExcelCellFormattedValue = (worksheet, rowIndex, colIndex) => {
  if (!worksheet || colIndex < 0) return null;
  const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
  const cell = worksheet[cellAddress];
  if (!cell?.w) return null;
  const formatted = cell.w.toString().trim();
  return formatted || null;
};

const EXPIRY_MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const EXPIRY_MONTH_ABBR_TO_NUM = Object.fromEntries(
  EXPIRY_MONTH_ABBR.map((name, i) => [name.toLowerCase(), i + 1])
);

const isMmmYyFormattedValue = (value) =>
  typeof value === 'string' && /^[A-Za-z]{3}-\d{2}$/.test(value.trim());

/** Expiry cells often display as May-27 while storing 01-05-2027 — use serial when abbreviated */
const getExcelExpiryValue = (worksheet, rowIndex, colIndex, rawFallback) => {
  if (!worksheet || colIndex < 0) return rawFallback;
  const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
  const cell = worksheet[cellAddress];
  if (!cell) return rawFallback;

  const formatted = cell.w?.toString().trim();
  if (formatted && isMmmYyFormattedValue(formatted)) {
    if (cell.t === 'n' && typeof cell.v === 'number') return cell.v;
    if (cell.t === 'd' && cell.v instanceof Date) return cell.v;
    return formatted;
  }

  if (formatted) return formatted;
  return rawFallback;
};

const normalizeExcelHeaderName = (value) => {
  if (value === null || value === undefined) return '';
  return value.toString().trim().toLowerCase();
};

/** Prefer raw numeric cell value (cell.v) over formatted display ($50.00) */
const getExcelCellNumericValue = (worksheet, rowIndex, colIndex, rawFallback) => {
  if (worksheet && rowIndex >= 0 && colIndex >= 0) {
    const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
    const cell = worksheet[cellAddress];
    if (cell && (cell.t === 'n' || typeof cell.v === 'number')) {
      const num = Number(cell.v);
      if (!Number.isNaN(num)) return num;
    }
  }
  return parseExcelNumericString(rawFallback);
};

const parseExcelNumericString = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  const cleaned = value
    .toString()
    .trim()
    .replace(/[₹$€£¥,\s]/g, '')
    .replace(/^\((.+)\)$/, '-$1');
  const num = parseFloat(cleaned);
  return Number.isNaN(num) ? 0 : num;
};

/** Scan for the row that contains supplier-mapped column headers (skips blank/H rows above) */
const findExcelHeaderRowIndex = (jsonData, mappings) => {
  if (!Array.isArray(jsonData) || jsonData.length === 0) return -1;

  const productNameHeader = normalizeExcelHeaderName(mappings?.productName);
  if (!productNameHeader) return -1;

  const mappedColumnNames = Object.values(mappings || {})
    .filter((name) => name && name !== '-')
    .map((name) => normalizeExcelHeaderName(name));

  const maxScan = Math.min(jsonData.length, 50);
  let bestIndex = -1;
  let bestScore = 0;

  for (let r = 0; r < maxScan; r++) {
    const row = jsonData[r];
    if (!row || row.length === 0) continue;

    const rowHeaders = row
      .map((cell) => normalizeExcelHeaderName(cell))
      .filter(Boolean);

    if (!rowHeaders.includes(productNameHeader)) continue;

    let score = 0;
    for (const mappedName of mappedColumnNames) {
      if (rowHeaders.includes(mappedName)) score++;
    }

    const minScore = mappedColumnNames.length > 1 ? 2 : 1;
    if (score < minScore) continue;

    if (score > bestScore) {
      bestScore = score;
      bestIndex = r;
    }
  }

  return bestIndex;
};

const isExcelMetadataRow = (row) => {
  if (!row || row.length === 0) return true;
  const firstCell = row[0]?.toString().trim().toUpperCase();
  return firstCell === 'H' || firstCell === 'F';
};

const rowHasMappedProductValue = (row, colIndex) => {
  if (!row || colIndex < 0) return false;
  const value = row[colIndex];
  return value !== null && value !== undefined && value.toString().trim() !== '';
};

const parsePackedExpiryDate = (rawValue) => {
  const digits = rawValue?.toString().trim();
  if (!digits || !/^\d+$/.test(digits)) return null;

  let monthPart = '';
  let yearPart = '';

  // Some supplier software exports expiry like 1MMYYYY (e.g. 1072027 -> 07/2027)
  if (/^1(0[1-9]|1[0-2])\d{4}$/.test(digits)) {
    monthPart = digits.slice(1, 3);
    yearPart = digits.slice(3);
  } else if (/^(0[1-9]|1[0-2])\d{4}$/.test(digits)) {
    monthPart = digits.slice(0, 2);
    yearPart = digits.slice(2);
  } else {
    return null;
  }

  const month = parseInt(monthPart, 10);
  const year = parseInt(yearPart, 10);
  if (month < 1 || month > 12 || Number.isNaN(year)) return null;
  return new Date(year, month - 1, 1);
};

const parseDdMmYyyyDateParts = (day, month, year) => {
  const d = parseInt(day, 10);
  const m = parseInt(month, 10);
  let y = parseInt(year, 10);
  if (Number.isNaN(d) || Number.isNaN(m) || Number.isNaN(y)) return null;
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  if (y < 100) y += 2000;
  return new Date(y, m - 1, d);
};

/** Parse expiry from Excel — always treats dd-mm-yyyy / dd/mm/yyyy as day-month-year (Indian format) */
const parseExcelDate = (value) => {
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'number') {
    const packedExpiryDate = parsePackedExpiryDate(value);
    if (packedExpiryDate) return packedExpiryDate;

    // Excel serial dates for modern dates are well below 100000; avoid misreading packed values
    if (value > 0 && value < 100000) {
      const date = new Date(Math.round((value - 25569) * 864e5));
      const userTimezoneOffset = date.getTimezoneOffset() * 60000;
      return new Date(date.getTime() + userTimezoneOffset);
    }
    return null;
  }

  const trimmed = value.toString().trim();
  if (!trimmed) return null;

  const packedExpiryDate = parsePackedExpiryDate(trimmed);
  if (packedExpiryDate) return packedExpiryDate;

  const datePart = trimmed.split(/[T\s]/)[0].trim();

  // dd-mm-yyyy, dd/mm/yyyy, dd.mm.yyyy — day first (e.g. 01-07-2027 -> July 2027)
  let match = datePart.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})$/);
  if (match) {
    return parseDdMmYyyyDateParts(match[1], match[2], match[3]);
  }

  match = datePart.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
  if (match) {
    return new Date(parseInt(match[1], 10), parseInt(match[2], 10) - 1, parseInt(match[3], 10));
  }

  // mm-yyyy / mm/yyyy expiry-only values
  match = datePart.match(/^(\d{1,2})[-/.](\d{2,4})$/);
  if (match) {
    const m = parseInt(match[1], 10);
    let y = parseInt(match[2], 10);
    if (y < 100) y += 2000;
    if (m >= 1 && m <= 12) return new Date(y, m - 1, 1);
  }

  // MMM-YY display format from supplier Excel (e.g. May-27, Mar-28)
  match = datePart.match(/^([A-Za-z]{3})-(\d{2})$/);
  if (match) {
    const m = EXPIRY_MONTH_ABBR_TO_NUM[match[1].toLowerCase()];
    let y = parseInt(match[2], 10);
    if (m && !Number.isNaN(y)) {
      if (y < 100) y += 2000;
      return new Date(y, m - 1, 1);
    }
  }

  return null;
};

const formatExpiryDisplay = (parsedDate) =>
  `${EXPIRY_MONTH_ABBR[parsedDate.getMonth()]}-${String(parsedDate.getFullYear()).slice(-2)}`;

const parseExpiryMonthYear = (expiryDisplay, expiryIso) => {
  if (expiryIso) {
    const d = new Date(expiryIso);
    if (!Number.isNaN(d.getTime())) {
      return { month: d.getMonth() + 1, year: d.getFullYear() };
    }
  }

  const trimmed = (expiryDisplay || '').trim();
  if (!trimmed) return null;

  const mmmYy = trimmed.match(/^([A-Za-z]{3})-(\d{2})$/);
  if (mmmYy) {
    const month = EXPIRY_MONTH_ABBR_TO_NUM[mmmYy[1].toLowerCase()];
    const year = 2000 + parseInt(mmmYy[2], 10);
    if (month) return { month, year };
  }

  const numericValue = trimmed.replace(/\D/g, '');
  if (numericValue.length === 4) {
    const month = parseInt(numericValue.substring(0, 2), 10);
    const year = 2000 + parseInt(numericValue.substring(2, 4), 10);
    if (month >= 1 && month <= 12) return { month, year };
  }

  return null;
};

const buildSupplierProductMapNormIndex = (productMap) => {
  const index = new Map();
  if (!productMap || typeof productMap !== 'object') return index;
  for (const [key, val] of Object.entries(productMap)) {
    if (val == null || String(val).trim() === '') continue;
    const nk = normalizeNameForMatch(key);
    if (nk && !index.has(nk)) index.set(nk, String(val).trim());
  }
  return index;
};

const resolveInternalNameFromSupplierMap = (productMap, supplierNormIndex, supplierProductName) => {
  const raw = supplierProductName?.toString().trim();
  if (!raw) return '';
  const direct = productMap?.[raw];
  if (direct != null && String(direct).trim() !== '') return String(direct).trim();
  return supplierNormIndex.get(normalizeNameForMatch(raw)) || '';
};

const buildProductByNormalizedNameIndex = (allProducts) => {
  const byNorm = new Map();
  for (const p of allProducts) {
    const name = p.Item_Name ?? p.ItemName ?? '';
    const nk = normalizeNameForMatch(name);
    if (nk && !byNorm.has(nk)) byNorm.set(nk, p);
  }
  return byNorm;
};

/** Same shape as AsyncSelect options from loadUnmappedProductOptions */
const masterProductToUnmappedSelectOption = (product) => ({
  value: product.id,
  label: product.Item_Name ?? product.ItemName ?? '',
  ...product,
});

/**
 * Item master payload for quick-create from the unmapped modal.
 * Only ItemCode + Item_Name carry data; every other field keeps the
 * same defaults ProductMaster sends for a blank new product.
 */
const buildDefaultItemMasterPayload = (itemName) => ({
  ItemCode: itemName,
  Item_Name: itemName,
  Company: '',
  Chemical: 0,
  Section: '',
  Category: '',
  Schedule: '',
  Package: 0,
  Packing_Size: '',
  Unit: '',
  CostPerTest: 0,
  CostPerTestUnit: '',
  Mrp: 0,
  Rate: 0,
  Supplier: 0,
  Shelf: '',
  HsnCode: '',
  Barcode: '',
  ItemType: 'Pharmacy',
  TaxCode: '',
  ReorderLevel: 0,
  ReorderLevelUnit: '',
  Remarks: '',
  Discount1: 0,
  Discount2: 0,
  Discount3: 0,
});

const isElectron = () =>
  typeof window !== 'undefined' && window.process && window.process.type;

const getIpcRenderer = () => {
  if (!isElectron()) return null;
  return window.require('electron').ipcRenderer;
};

const pickPositiveClientId = (...candidates) => {
  for (const raw of candidates) {
    const value = Number(raw);
    if (Number.isFinite(value) && value > 0) return value;
  }
  return null;
};

const resolveClientId = async () => {
  const ipcRenderer = getIpcRenderer();

  if (ipcRenderer) {
    const [generalSettings, clientInfo] = await Promise.all([
      ipcRenderer.invoke('store-get', 'generalsettings'),
      ipcRenderer.invoke('store-get', 'clientInfo'),
    ]);

    return pickPositiveClientId(
      generalSettings?.ClientID,
      generalSettings?.clientID,
      clientInfo?.ClientID,
      clientInfo?.clientID,
      clientInfo?.ID,
      localStorage.getItem('cloud_selected_client_id'),
    );
  }

  initializeWebUserInfo();
  const web = getWebUserInfo();

  return pickPositiveClientId(
    web.generalSettings?.ClientID,
    web.generalSettings?.clientID,
    web.clientInfo?.ClientID,
    web.clientInfo?.clientID,
    web.clientInfo?.ID,
    localStorage.getItem('cloud_selected_client_id'),
  );
};

const normalizePackingFromBill = (packing, productFallback = '1') => {
  let result = packing ? packing.toString().trim() : (productFallback || '1');
  if (!result) return productFallback || '1';

  const strVal = result.toString().trim().toLowerCase();
  const forXMatch = strVal.replace(/\u00d7/g, 'x').replace(/\*/g, 'x');
  const isLiquid = /([0-9]+)\s*(ml|l)\b/.test(strVal) || /([0-9]+)\s*(ml|l)$/.test(strVal);
  const isGram = /([0-9]+)\s*(g|gm|GM)\b/.test(strVal) || /([0-9]+)\s*(g|gm|GM)$/.test(strVal);
  const xPackingMatch = forXMatch.match(/^(\d+)\s*x\s*(\d+)/i);

  if (isLiquid || isGram) {
    return '1';
  }
  if (xPackingMatch) {
    return xPackingMatch[2];
  }
  const match = strVal.match(/(\d+)/);
  if (match) {
    return match[0];
  }
  return result;
};

/** Pre-fill mapping modal when supplier name matches item master (normalized name) */
const buildUnmappedExactNamePrefill = (unmappedNames, allProducts) => {
  if (!unmappedNames?.length || !allProducts?.length) return {};
  const byNorm = buildProductByNormalizedNameIndex(allProducts);
  const prefill = {};
  for (const supplierProductName of unmappedNames) {
    const p = byNorm.get(normalizeNameForMatch(supplierProductName));
    if (p) prefill[supplierProductName] = masterProductToUnmappedSelectOption(p);
  }
  return prefill;
};

const PurchaseEntryForm = ({ mode = 'create', editParams = null, tabId = null }) => {
  const isEditMode = mode === 'edit';
  const tabContext = useContext(TabContext);
  const editLoadRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [aiExtracting, setAiExtracting] = useState(false);
  const [purchaseNumber, setPurchaseNumber] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [entryDate, setEntryDate] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [note, setNote] = useState('');
  const [paymentType, setPaymentType] = useState('debit');
  const [dueDate, setDueDate] = useState('');
  const [entryDateISO, setEntryDateISO] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [productsMaster, setProductsMaster] = useState([]);

  // Product specific states
  const [products, setProducts] = useState([]);

  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [printAfterSave, setPrintAfterSave] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentModalSelection, setPaymentModalSelection] = useState('debit');
  const [summaryDiscountAmount, setSummaryDiscountAmount] = useState('');
  const [summaryDiscountPercentage, setSummaryDiscountPercentage] = useState('');
  const [isRounded, setIsRounded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [showSupplierProductMappingModal, setShowSupplierProductMappingModal] = useState(false);
  const [pendingExcelUploadContext, setPendingExcelUploadContext] = useState(null);
  const [unmappedSupplierProducts, setUnmappedSupplierProducts] = useState([]);
  const [selectedUnmappedProductMap, setSelectedUnmappedProductMap] = useState({});
  const [isSavingSupplierProductMappings, setIsSavingSupplierProductMappings] = useState(false);
  const [unmappedItemsToCreate, setUnmappedItemsToCreate] = useState([]);
  const [isCreatingUnmappedItems, setIsCreatingUnmappedItems] = useState(false);
  const [isReloadingUnmappedMatches, setIsReloadingUnmappedMatches] = useState(false);

  // Product search input state for new entry row
  const [productSearch, setProductSearch] = useState('');
  const [showProductList, setShowProductList] = useState(false);
  const productSearchInputRef = useRef(null);
  const productSearchContainerRef = useRef(null);
  const productDropdownRef = useRef(null);
  const productDropdownScrollRef = useRef(null);
  const productRowRefs = useRef([]);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0, height: 400, position: 'bottom' });
  const [selectedProductIndex, setSelectedProductIndex] = useState(-1);
  const productSearchDebounceRef = useRef(null);

  // State for editing product in existing rows
  const [editingProductRowIndex, setEditingProductRowIndex] = useState(null);
  const [productSearchForEdit, setProductSearchForEdit] = useState('');
  const productNameCellRefs = useRef([]);

  const token = useMemo(() => localStorage.getItem('accessToken'), []);

  const getSupplierCreditDays = (supplier) => {
    if (!supplier) return 0;
    const creditRaw =
      supplier.CreditDays ??
      supplier.creditDays ??
      supplier.Credit_Days ??
      supplier.Credit_Day ??
      supplier.credit_day ??
      supplier.CreditDay ??
      0;

    const creditDays = parseInt(creditRaw, 10);
    return Number.isNaN(creditDays) ? 0 : creditDays;
  };

  // Refs
  const invoiceNumberRef = useRef(null);
  const invoiceDateRef = useRef(null);
  const dueDateRef = useRef(null);
  const supplierRef = useRef(null);
  const paymentTypeRef = useRef(null);
  const noteRef = useRef(null);
  const productRefs = useRef([]);
  const batchRefs = useRef([]);
  const expiryRefs = useRef([]);
  const packingRefs = useRef([]);
  const mrpRefs = useRef([]);
  const qtyRefs = useRef([]);
  const focRefs = useRef([]);
  const rateRefs = useRef([]);
  const discountRefs = useRef([]);
  const discountPercentageRefs = useRef([]);
  const gstRateRefs = useRef([]);
  const hsnCodeRefs = useRef([]);
  const submitbtnRef = useRef(null);
  const entryDateRef = useRef(null);
  const paymentModalCashButtonRef = useRef(null);
  const paymentModalCreditButtonRef = useRef(null);
  const isSavingRef = useRef(false); // Track if save operation is in progress
  const fileInputRef = useRef(null);
  const pdfFileInputRef = useRef(null);

  // Removed auto-focus on product search - flow is now: Supplier → Invoice Number → Invoice Date → Payment Modal → Product Search

  // Helper function to calculate dropdown position based on available space
  const calculateDropdownPosition = (rect) => {
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const dropdownHeight = 400; // Fixed height
    const gap = 4; // Gap between input and dropdown

    let top, position;

    // Check if there's enough space below
    if (spaceBelow >= dropdownHeight + gap) {
      // Position below
      position = 'bottom';
      top = rect.bottom + window.scrollY + gap;
    } else if (spaceAbove >= dropdownHeight + gap) {
      // Position above
      position = 'top';
      top = rect.top + window.scrollY - dropdownHeight - gap;
    } else {
      // Not enough space on either side, use the side with more space
      if (spaceBelow > spaceAbove) {
        position = 'bottom';
        top = rect.bottom + window.scrollY + gap;
      } else {
        position = 'top';
        top = rect.top + window.scrollY - dropdownHeight - gap;
      }
    }

    return {
      top,
      left: rect.left + window.scrollX,
      width: Math.max(rect.width, 1000),
      height: dropdownHeight,
      position
    };
  };

  // Update dropdown position on scroll and resize
  useEffect(() => {
    if (!showProductList || !productSearchContainerRef.current) return;

    const updatePosition = () => {
      if (productSearchContainerRef.current) {
        const rect = productSearchContainerRef.current.getBoundingClientRect();
        setDropdownPosition(calculateDropdownPosition(rect));
      }
    };

    updatePosition(); // Initial calculation
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showProductList]);

  const defaultHeaders = useMemo(() => {
    const headers = {
      'XApiKey': process.env.REACT_APP_API_KEY,
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }, [token]);

  const fetchMasters = useCallback(async () => {
    setLoading(true);
    try {
      const [supplierResponse, baseDataResponse] = await Promise.all([
        axios.get(`${BASE_URL}${GET_SUPPLIER_MASTER}`, {
          headers: {
            ...defaultHeaders,
            FilterText: '',
          },
        }),
        axios.get(`${BASE_URL}${GET_BASE_DATA}`, {
          headers: {
            ...defaultHeaders,
          },
        }),
      ]);

      if (supplierResponse.status === 200) {
        setSuppliers(supplierResponse.data?.data || []);
      }

      if (baseDataResponse.status === 200 && mode === 'create') {
        setPurchaseNumber(baseDataResponse.data.data.nextNum[0]?.NextNum);
      }
    } catch (error) {
      console.error('Failed to fetch purchase masters:', error);
      toast.error('Could not load suppliers', {
        autoClose: 1500,
      });
    } finally {
      setLoading(false);
    }
  }, [defaultHeaders, mode]);

  useEffect(() => {
    fetchMasters();
  }, [fetchMasters]);

  // Load purchase data for editing
  const loadPurchaseDataForEdit = useCallback(async (invoiceNo, sequence, editNo) => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_PURCHASE_ALL_DATA}`, {
        params: {
          _sequence: sequence,
          invoiceNo: invoiceNo,
          editNo: editNo,
          docType: 'Pharmacy',
        },
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY, 
          'Authorization': `Bearer ${token}`,
        }
      });

      if (response.status === 200 && response.data?.isSucess) {
        const data = response.data.data;
        const header = data.invHeader?.[0];
        const lineDetails = data.invLineDetail || [];
        const batchDetails = data.invBatchDetails || [];

        if (header) {
          setPurchaseNumber(invoiceNo)
          // Set header fields
          setInvoiceNumber(header.PurchaseRefNo || '');

          // Format invoice date - extract date part directly from ISO string to avoid timezone issues
          const invoiceDateValue = header.InvoiceDate || header.PurchaseDate;
          if (invoiceDateValue) {
            const invoiceDateStr = typeof invoiceDateValue === 'string' && invoiceDateValue.includes('T')
              ? invoiceDateValue.slice(0, 10) // Extract YYYY-MM-DD from ISO string
              : formatDateString(invoiceDateValue);
            setInvoiceDate(invoiceDateStr || formatDateString(new Date()));
          } else {
            setInvoiceDate(formatDateString(new Date()));
          }

          // Format purchase/entry date
          const purchaseDateValue = header.PurchaseDate;
          if (purchaseDateValue) {
            const purchaseDateStr = typeof purchaseDateValue === 'string' && purchaseDateValue.includes('T')
              ? purchaseDateValue.slice(0, 10) // Extract YYYY-MM-DD from ISO string
              : formatDateString(purchaseDateValue);
            setEntryDate(purchaseDateStr || formatDateString(new Date()));
            setEntryDateISO(purchaseDateStr || formatDateString(new Date()));
          } else {
            const today = formatDateString(new Date());
            setEntryDate(today);
            setEntryDateISO(today);
          }
          setNote(header.Remarks || '');
          setPaymentType(header.PaymentMode || 'debit');
          setDueDate(formatDateString(header.DueDate) || '');

          // Set supplier
          if (header.SupplierID && suppliers.length > 0) {
            const supplier = suppliers.find(
              (s) =>
                (s.SupplierID ?? s.supplierID ?? s.Id ?? s.id ?? s.SupplierId) === header.SupplierID
            );
            if (supplier) {
              setSelectedSupplier({
                value: header.SupplierID,
                label: header.SupplierName || supplier.SupplierName || supplier.Name || '',
                ...supplier,
              });
            }
          }

          // Summary/footer discount: header total often repeats line discounts. Only put the
          // remainder (true extra footer discount) in summaryDiscountAmount so we do not
          // double-count with line calculatedDiscountAmount in the cash-discount total.
          const lineDiscountSumFromApi = lineDetails.reduce(
            (sum, line) => sum + (parseFloat(line.DiscountAmount) || 0),
            0
          );
          const headerDisc = parseFloat(header.DiscountAmount) || 0;
          const discEps = 0.02;
          let summaryAmt = '';
          if (headerDisc > discEps) {
            if (lineDiscountSumFromApi < discEps) {
              summaryAmt = header.DiscountAmount.toString();
            } else if (headerDisc > lineDiscountSumFromApi + discEps) {
              summaryAmt = (headerDisc - lineDiscountSumFromApi).toFixed(2);
            }
          }
          setSummaryDiscountAmount(summaryAmt);
          setSummaryDiscountPercentage('');

          // Map line details to products
          const mappedProducts = lineDetails.map((line, index) => {
            // Find corresponding batch detail
            const batchDetail = batchDetails.find(
              (b) => b.Item_No === line.ItemNo && b.BatchCode === line.BatchCode
            );

            // Get package size
            const packageSize = line.PackageSize || line.Packing || 1;

            // REVERSE CALCULATION: Understanding the save flow
            // When saving:
            // - Quantity = getTotalUnits = (qty + foc) * packageSize (TOTAL units including FOC)
            // - Foc = getFocUnits = foc * packageSize (FOC units only)
            // 
            // So when loading, we need to reverse:
            // - Total units = Quantity (includes both paid and free)
            // - Free units = Foc
            // - Paid units = Quantity - Foc
            const totalUnits = line.Quantity || 0;  // Total units (paid + free)
            const freeUnits = line.FOC || 0;       // Free units only
            const paidUnits = totalUnits - freeUnits; // Paid units = total - free

            // Convert units to packages (strips) - same as purchase entry
            const qty = packageSize > 0 ? paidUnits / packageSize : 0;  // Paid packages
            const foc = packageSize > 0 ? freeUnits / packageSize : 0;   // Free packages

            // REVERSE RATE CALCULATION:
            // API gives us Total = amount BEFORE discount (qty * rate)
            // So: rate = Total / qty (if qty > 0)
            const originalAmount = line.Total || 0; // Amount before discount = qty * rate
            const calculatedRate = qty > 0 ? originalAmount / qty : 0;

            // Fallback: If calculated rate is 0 or invalid, use Price * packageSize
            // Price from API is unit rate, convert to package rate
            const fallbackRate = (line.Price || 0) * packageSize;
            const rate = calculatedRate > 0 ? calculatedRate : fallbackRate;

            // MRP: API gives unit MRP, convert to package MRP
            const unitMrp = line.MRP || 0;
            const mrp = unitMrp * packageSize;

            // API stores both DiscountAmount and DiscountPercentage when discount was entered as %.
            // calculateDiscountAmount() adds % and fixed amount, so loading both multiplies the discount.
            // When a line has a percentage, derive discount from % only on edit load.
            const pctRaw = parseFloat(line.DiscountPercentage);
            const hasPct = !Number.isNaN(pctRaw) && pctRaw > 0;
            const discountPercentage = hasPct ? line.DiscountPercentage.toString() : '';
            const discountAmount =
              hasPct
                ? ''
                : line.DiscountAmount && line.DiscountAmount > 0
                  ? line.DiscountAmount.toString()
                  : '';

            let expiryDisplay = '';
            let expiry = '';
            if (batchDetail?.ExpiryDate) {
              expiry = formatDateString(batchDetail.ExpiryDate);
              if (expiry) {
                const expiryDate = new Date(expiry);
                if (!Number.isNaN(expiryDate.getTime())) {
                  expiryDisplay = formatExpiryDisplay(expiryDate);
                }
              }
            }

            const product = {
              id: line.ItemNo,
              product: { value: line.ItemNo, label: line.ItemName || '' },
              name: line.ItemName || '',
              packing: packageSize.toString(),
              packageSize: packageSize,
              unit: line.Unit || '',
              gstRate: parseFloat(line.TaxCode) || 0,
              taxCode: line.TaxCode || '',
              rate: calculatedRate, // Package rate (before discount)
              mrp: mrp, // Package MRP
              hsnCode: line.HsnCode || '',
              batch: line.BatchCode || '',
              expiry: expiry,
              expiryDisplay: expiryDisplay,
              qty: qty.toString(), // Packages/strips
              foc: foc.toString(), // Free packages/strips
              discountAmount: discountAmount,
              discountPercentage: discountPercentage,
            };

            // Do not use summaryDiscountPercentage from React state here — setState is async and
            // would be stale; footer % is cleared on load and not read from the API.
            return recalculateProductRow(product, 0);
          });

          setProducts(mappedProducts);
          toast.success('Purchase data loaded for editing', { autoClose: 1500 });
        }
      } else {
        toast.error('Failed to load purchase data', { autoClose: 1500 });
      }
    } catch (error) {
      console.error('Failed to load purchase data:', error);
      toast.error('Failed to load purchase data', { autoClose: 1500 });
    } finally {
      setLoading(false);
    }
  }, [defaultHeaders, suppliers, token]);

  // Load edit purchase when suppliers are ready (edit page only)
  useEffect(() => {
    if (!isEditMode || !editParams?.invoiceNo || !editParams?.sequence || suppliers.length === 0) {
      return;
    }

    const loadKey = `${editParams.invoiceNo}_${editParams.sequence}_${editParams.editNo ?? 0}`;
    if (editLoadRef.current === loadKey) {
      return;
    }

    editLoadRef.current = loadKey;
    loadPurchaseDataForEdit(editParams.invoiceNo, editParams.sequence, editParams.editNo ?? 0);
  }, [isEditMode, editParams, suppliers.length, loadPurchaseDataForEdit]);

  useEffect(() => {
    supplierRef.current?.focus();
  }, []);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (productSearchDebounceRef.current) {
        clearTimeout(productSearchDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleWheel = (e) => {
      if (e.target.type === 'number') {
        e.target.blur();
      }
    };

    const handleKeyDown = (e) => {
      if (e.target.type === 'number') {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
        }
      }
    };

    document.addEventListener('wheel', handleWheel, { capture: true });
    document.addEventListener('keydown', handleKeyDown, { capture: true });

    return () => {
      document.removeEventListener('wheel', handleWheel, { capture: true });
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, []);

  useEffect(() => {
    // Only set default dates if not in edit mode
    // In edit mode, dates will be set by loadPurchaseDataForEdit
    if (!isEditMode) {
      const today = formatDateString(new Date());
      // Set entry date
      setEntryDate(today);
      setEntryDateISO(today);
      setInvoiceDate(today);
    }
  }, [isEditMode]);

  useEffect(() => {
    if (!invoiceDate) {
      setDueDate('');
      return;
    }

    const baseDate = new Date(invoiceDate);
    if (Number.isNaN(baseDate.getTime())) {
      setDueDate('');
      return;
    }

    const creditDays = getSupplierCreditDays(selectedSupplier);
    baseDate.setDate(baseDate.getDate() + creditDays);
    const formattedDueDate = formatDateString(baseDate);
    setDueDate(formattedDueDate || '');
  }, [invoiceDate, selectedSupplier]);

  // Recalculate all products when summary discount percentage changes
  useEffect(() => {
    let summaryDiscountPct = parseFloat(summaryDiscountPercentage) || 0;
    if (summaryDiscountPct < 0) summaryDiscountPct = 0;
    if (summaryDiscountPct > 100) summaryDiscountPct = 100;
    setProducts((prevProducts) =>
      prevProducts.map((product) => {
        // Inline recalculation to avoid dependency issues
        const qty = parseFloat(product.qty) || 0;
        const rate = parseFloat(product.rate) || 0;
        const amount = qty * rate;

        // Inline calculateDiscountAmount logic
        let discountAmount = 0;

        // Apply percentage discount first if provided
        if (product.discountPercentage && product.discountPercentage.toString().trim() !== '') {
          const percentage = parseFloat(product.discountPercentage);
          if (!Number.isNaN(percentage) && percentage > 0) {
            discountAmount += (amount * percentage) / 100;
          }
        }

        // Apply fixed amount discount if provided
        if (product.discountAmount && product.discountAmount.toString().trim() !== '') {
          const fixedAmount = parseFloat(product.discountAmount);
          if (!Number.isNaN(fixedAmount) && fixedAmount > 0) {
            discountAmount += Math.min(fixedAmount, amount - discountAmount);
          }
        }

        discountAmount = Math.min(discountAmount, amount);

        let discountedAmount = amount - discountAmount;

        // Apply summary discount percentage if provided
        let summaryDiscountAmount = 0;
        if (summaryDiscountPct > 0) {
          summaryDiscountAmount = (discountedAmount * summaryDiscountPct) / 100;
          discountedAmount = discountedAmount - summaryDiscountAmount;
        }

        const gstRate = parseFloat(product.gstRate) || 0;
        const sgst = (discountedAmount * (gstRate / 2)) / 100;
        const cgst = (discountedAmount * (gstRate / 2)) / 100;
        const totalAmount = discountedAmount + sgst + cgst;
        const taxAmount = sgst + cgst;

        // Inline getPackageSize logic
        const parsePackingQuantity = (value, fallback = 1) => {
          if (value === null || value === undefined || value === '') return fallback;
          if (typeof value === 'number') {
            return value > 0 ? value : fallback;
          }
          const numeric = parseFloat(value.toString().replace(/[^0-9.]/g, ''));
          return Number.isNaN(numeric) || numeric <= 0 ? fallback : numeric;
        };
        const packageSize = parsePackingQuantity(product.packageSize ?? product.packing, 1);

        const mrpValue = parseFloat(product.mrp) || 0;
        const unitMrp = packageSize ? mrpValue / packageSize : mrpValue;

        return {
          ...product,
          packageSize,
          amount: amount.toFixed(2),
          discountAmount: discountAmount.toFixed(2),
          summaryDiscountAmount: summaryDiscountAmount.toFixed(2),
          discountedAmount: discountedAmount.toFixed(2),
          sgst: sgst.toFixed(2),
          cgst: cgst.toFixed(2),
          totalAmount: totalAmount.toFixed(2),
          taxAmount: taxAmount.toFixed(2),
          unitMrp: Number.isFinite(unitMrp) ? Number(unitMrp.toFixed(4)) : 0,
        };
      })
    );
  }, [summaryDiscountPercentage]);

  const handleKeyDown = (e, nextFieldRef, fallbackRef = null) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (nextFieldRef?.current) {
        nextFieldRef.current.focus();
      } else if (fallbackRef?.current) {
        fallbackRef.current.focus();
      }
    }
  };

  const handleInvoiceDateKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setShowPaymentModal(true);
    } else {
      handleKeyDown(e, dueDateRef);
    }
  };

  const handleClosePaymentModal = useCallback(() => {
    setShowPaymentModal(false);
    setTimeout(() => {
      invoiceDateRef.current?.focus();
    }, 0);
  }, [invoiceDateRef]);

  const handlePaymentModeSelection = useCallback(
    (mode) => {
      setPaymentModalSelection(mode);
      setPaymentType(mode);
      setShowPaymentModal(false);
      setTimeout(() => {
        if (productSearchInputRef.current) {
          productSearchInputRef.current.focus();
          setShowProductList(true);
          setSelectedProductIndex(0);
          // Calculate position for dropdown
          if (productSearchContainerRef.current) {
            const rect = productSearchContainerRef.current.getBoundingClientRect();
            setDropdownPosition(calculateDropdownPosition(rect));
          }
        }
      }, 0);
    },
    []
  );

  useEffect(() => {
    if (!showPaymentModal) return undefined;

    const targetSelection = paymentType === 'credit' ? 'credit' : 'debit';
    setPaymentModalSelection((current) =>
      current === targetSelection ? current : targetSelection
    );

    const focusTimeout = setTimeout(() => {
      const initialRef =
        targetSelection === 'credit'
          ? paymentModalCreditButtonRef.current
          : paymentModalCashButtonRef.current;
      (initialRef ?? paymentModalCashButtonRef.current)?.focus();
    }, 0);

    return () => clearTimeout(focusTimeout);
  }, [showPaymentModal, paymentType]);

  useEffect(() => {
    if (!showPaymentModal) return;

    const targetRef =
      paymentModalSelection === 'credit'
        ? paymentModalCreditButtonRef.current
        : paymentModalCashButtonRef.current;

    targetRef?.focus();
  }, [paymentModalSelection, showPaymentModal]);

  useEffect(() => {
    if (!showPaymentModal) return undefined;

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleClosePaymentModal();
      }
    };

    document.addEventListener('keydown', handleEscapeKey, true);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey, true);
    };
  }, [showPaymentModal, handleClosePaymentModal]);

  const handlePaymentModalKeyDown = useCallback(
    (event) => {
      const { key } = event;

      if (key === 'ArrowRight' || key === 'ArrowDown') {
        event.preventDefault();
        setPaymentModalSelection('credit');
        return;
      }

      if (key === 'ArrowLeft' || key === 'ArrowUp') {
        event.preventDefault();
        setPaymentModalSelection('debit');
        return;
      }

      if (key === 'Enter') {
        event.preventDefault();
        handlePaymentModeSelection(paymentModalSelection);
        return;
      }
    },
    [setPaymentModalSelection, paymentModalSelection, handlePaymentModeSelection]
  );

  // Calculate discount amount based on discount amount and percentage
  const calculateDiscountAmount = (amount, discountAmount, discountPercentage) => {
    let totalDiscount = 0;
    const maxAmount = Math.max(0, parseFloat(amount) || 0);

    // Apply percentage discount first if provided — clamp to 0..100
    if (discountPercentage && discountPercentage.toString().trim() !== '') {
      let percentage = parseFloat(discountPercentage);
      if (!Number.isNaN(percentage)) {
        if (percentage < 0) percentage = 0;
        if (percentage > 100) percentage = 100;
        if (percentage > 0) {
          totalDiscount += (maxAmount * percentage) / 100;
        }
      }
    }

    // Apply fixed amount discount if provided — clamp to remaining amount
    if (discountAmount && discountAmount.toString().trim() !== '') {
      let fixedAmount = parseFloat(discountAmount);
      if (!Number.isNaN(fixedAmount) && fixedAmount > 0) {
        if (fixedAmount < 0) fixedAmount = 0;
        totalDiscount += Math.min(fixedAmount, Math.max(0, maxAmount - totalDiscount));
      }
    }

    return Math.min(totalDiscount, maxAmount);
  };

  const calculateDiscountPercentage = (discountPercentage) => {
    if (!discountPercentage) return 0;
    const percentage = parseFloat(discountPercentage);
    return Number.isNaN(percentage) ? 0 : percentage;
  };

  const parsePackingQuantity = (value, fallback = 1) => {
    if (value === null || value === undefined || value === '') return fallback;
    if (typeof value === 'number') {
      return value > 0 ? value : fallback;
    }
    const numeric = parseFloat(value.toString().replace(/[^0-9.]/g, ''));
    return Number.isNaN(numeric) || numeric <= 0 ? fallback : numeric;
  };

  const formatDateString = (value) => {
    if (!value) return null;

    if (typeof value === 'string') {
      const trimmed = value.trim();
      // If already in YYYY-MM-DD format, return as is
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        return trimmed;
      }
      // If in ISO format with time (YYYY-MM-DDTHH:MM:SS), extract just the date part
      if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed)) {
        return trimmed.slice(0, 10);
      }
    }

    // For Date objects or other formats, use local date to avoid timezone issues
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }

    // Use local date components instead of toISOString() to avoid timezone conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getPackageSize = (product) =>
    parsePackingQuantity(product.packageSize ?? product.packing, 1);

  const getTotalUnits = (product) => {
    const qty = parseFloat(product.qty) || 0;
    const focQty = parseFloat(product.foc) || 0;
    const packageSize = getPackageSize(product);
    return (qty + focQty) * packageSize;
  };

  // Original unit rate calculation (without FOC consideration)
  const getUnitRate = (product) => {
    const rate = parseFloat(product.rate) || 0;
    const packageSize = getPackageSize(product);
    return packageSize ? rate / packageSize : rate;
  };

  const getUnitMrp = (product) => {
    const mrpValue = parseFloat(product.mrp) || 0;
    const packageSize = getPackageSize(product);
    return packageSize ? mrpValue / packageSize : mrpValue;
  };

  // Effective unit rate calculation (with FOC consideration)
  const getEffectiveUnitRate = (product) => {
    const qty = parseFloat(product.qty) || 0;
    const foc = parseFloat(product.foc) || 0;
    const rate = parseFloat(product.rate) || 0;
    const packageSize = getPackageSize(product);

    // Calculate total amount paid (after discount)
    const amount = qty * rate;

    // Calculate discount amount (inline logic to avoid scope issues)
    let discountAmount = 0;
    if (product.discount) {
      const discountStr = product.discount.toString().trim();
      if (discountStr.endsWith('p') || discountStr.endsWith('%')) {
        const percentage = parseFloat(discountStr.slice(0, -1));
        if (!Number.isNaN(percentage)) {
          discountAmount = (amount * percentage) / 100;
        }
      } else {
        const fixedAmount = parseFloat(discountStr);
        if (!Number.isNaN(fixedAmount)) {
          discountAmount = Math.min(fixedAmount, amount);
        }
      }
    }

    const discountedAmount = amount - discountAmount;

    // Calculate total units received (including FOC)
    const totalUnits = (qty + foc) * packageSize;

    // Return effective unit rate (amount paid / total units received)
    // This accounts for FOC - if you get free items, the effective price per unit decreases
    if (totalUnits > 0) {
      return discountedAmount / totalUnits;
    }

    // Fallback to original calculation if no units
    return packageSize ? rate / packageSize : rate;
  };

  const getFocUnits = (product) => {
    const focQty = parseFloat(product.foc) || 0;
    const packageSize = getPackageSize(product);
    return focQty * packageSize;
  };

  const recalculateProductRow = (product, summaryDiscountPercentage = 0) => {
    const qty = parseFloat(product.qty) || 0;
    const rate = parseFloat(product.rate) || 0;
    const amount = qty * rate;
    const calculatedDiscountAmount = calculateDiscountAmount(amount, product.discountAmount || '', product.discountPercentage || '');
    let discountedAmount = amount - calculatedDiscountAmount;

    // Apply summary discount percentage if provided
    let summaryDiscountAmount = 0;
    if (summaryDiscountPercentage > 0) {
      summaryDiscountAmount = (discountedAmount * summaryDiscountPercentage) / 100;
      discountedAmount = discountedAmount - summaryDiscountAmount;
    }

    const gstRate = parseFloat(product.gstRate) || 0;
    const sgst = (discountedAmount * (gstRate / 2)) / 100;
    const cgst = (discountedAmount * (gstRate / 2)) / 100;
    const totalAmount = discountedAmount + sgst + cgst;
    const taxAmount = sgst + cgst;
    const packageSize = getPackageSize(product);
    const unitMrp = getUnitMrp(product);

    return {
      ...product,
      packageSize,
      amount: amount.toFixed(2),
      calculatedDiscountAmount: calculatedDiscountAmount.toFixed(2),
      summaryDiscountAmount: summaryDiscountAmount.toFixed(2),
      discountedAmount: discountedAmount.toFixed(2), // Amount after all discounts, before tax
      sgst: sgst.toFixed(2),
      cgst: cgst.toFixed(2),
      totalAmount: totalAmount.toFixed(2),
      taxAmount: taxAmount.toFixed(2),
      unitMrp: Number.isFinite(unitMrp) ? Number(unitMrp.toFixed(4)) : 0,
    };
  };

  const getSupplierColumnMappings = (supplier) =>
    supplier?.Mappings?.columns ||
    supplier?.mappings?.columns ||
    supplier?.Mappings ||
    supplier?.mappings ||
    {};

  const getSupplierProductMappings = (supplier) =>
    supplier?.Products ||
    supplier?.products ||
    supplier?.Mappings?.products ||
    supplier?.mappings?.products ||
    {};

  const buildProductRowsFromExcel = useCallback((jsonData, mappings, productMap, allProducts, worksheet = null) => {
    if (!Array.isArray(jsonData) || jsonData.length < 2) {
      return { newProducts: [], unmappedNames: [] };
    }

    const headerRowIndex = findExcelHeaderRowIndex(jsonData, mappings);
    if (headerRowIndex === -1) {
      return { newProducts: [], unmappedNames: [] };
    }

    const headerRow = jsonData[headerRowIndex];
    const columnMap = {};
    headerRow.forEach((col, index) => {
      if (col !== null && col !== undefined && col !== '') {
        columnMap[normalizeExcelHeaderName(col)] = index;
      }
    });

    const getColIndex = (mappingKey) => {
      const mappedName = mappings?.[mappingKey];
      if (!mappedName || mappedName === '-') return -1;
      return columnMap[mappedName.toString().trim().toLowerCase()] ?? -1;
    };

    const nameIdx = getColIndex('productName');
    if (nameIdx === -1) {
      return { newProducts: [], unmappedNames: [] };
    }

    const batchIdx = getColIndex('batch');
    const expiryIdx = getColIndex('expiry');
    const packingIdx = getColIndex('packing');
    const qtyIdx = getColIndex('qty');
    const focIdx = getColIndex('foc');
    const rateIdx = getColIndex('rate');
    const mrpIdx = getColIndex('mrp');
    const discPctIdx = getColIndex('discount%');
    const discAmtIdx = getColIndex('discount');
    const gstIdx = getColIndex('gstRate');
    const cgstIdx = getColIndex('cgst');
    const sgstIdx = getColIndex('sgst');
    const hsnIdx = getColIndex('hsnCode');

    const summaryDiscountPct = parseFloat(summaryDiscountPercentage) || 0;
    const newProducts = [];
    const unmappedNames = [];
    const seenUnmapped = new Set();

    const supplierNormIndex = buildSupplierProductMapNormIndex(productMap);
    const productByNormName = buildProductByNormalizedNameIndex(allProducts);

    for (let i = headerRowIndex + 1; i < jsonData.length; i++) {
      const row = jsonData[i];
      if (!row || row.length === 0) continue;
      if (isExcelMetadataRow(row)) continue;
      if (!rowHasMappedProductValue(row, nameIdx)) continue;

      const supplierProductName = row[nameIdx]?.toString().trim();
      if (!supplierProductName) continue;

      let internalProductName = resolveInternalNameFromSupplierMap(
        productMap,
        supplierNormIndex,
        supplierProductName
      );
      if (!internalProductName) {
        const nameMatchProduct = productByNormName.get(normalizeNameForMatch(supplierProductName));
        if (nameMatchProduct) {
          internalProductName = String(
            nameMatchProduct.Item_Name ?? nameMatchProduct.ItemName ?? ''
          ).trim();
        }
      }
      if (!internalProductName) {
        if (!seenUnmapped.has(supplierProductName)) {
          seenUnmapped.add(supplierProductName);
          unmappedNames.push(supplierProductName);
        }
        continue;
      }

      const product = productByNormName.get(normalizeNameForMatch(internalProductName));

      if (!product) {
        if (!seenUnmapped.has(supplierProductName)) {
          seenUnmapped.add(supplierProductName);
          unmappedNames.push(supplierProductName);
        }
        continue;
      }

      const batch = batchIdx !== -1 ? row[batchIdx]?.toString() : '';

      let expiry = '';
      let expiryDisplay = '';
      if (expiryIdx !== -1) {
        const rawExpiry = getExcelExpiryValue(worksheet, i, expiryIdx, row[expiryIdx]);
        const parsedDate = parseExcelDate(rawExpiry);
        if (parsedDate) {
          expiry = formatDateString(parsedDate);
          expiryDisplay = formatExpiryDisplay(parsedDate);
        }
      }

      let packing = packingIdx !== -1 ? row[packingIdx]?.toString() : (product.Package || product.package || product.packing || '1');
      if (packing) {
        const strVal = packing.toString().trim().toLowerCase();
        // Excel often uses × (multiply) or *; normalize so 1×20 / 1*20 match like 1x20 / 1X20
        const forXMatch = strVal.replace(/\u00d7/g, 'x').replace(/\*/g, 'x');
        const isLiquid = /([0-9]+)\s*(ml|l)\b/.test(strVal) || /([0-9]+)\s*(ml|l)$/.test(strVal);
        const isGram = /([0-9]+)\s*(g|gm|GM)\b/.test(strVal) || /([0-9]+)\s*(g|gm|GM)$/.test(strVal);
        const xPackingMatch = forXMatch.match(/^(\d+)\s*x\s*(\d+)/i);

        if (isLiquid || isGram) {
          packing = '1';
        } else if (xPackingMatch) {
          // NxM = N packs of M units → use M (e.g. 1X20 → 20, 1x10 → 10)
          packing = xPackingMatch[2];
        } else {
          const match = strVal.match(/(\d+)/);
          if (match) {
            packing = match[0];
          }
        }
      }

      const qty = qtyIdx !== -1 ? getExcelCellNumericValue(worksheet, i, qtyIdx, row[qtyIdx]) : 0;
      const foc = focIdx !== -1 ? getExcelCellNumericValue(worksheet, i, focIdx, row[focIdx]) : 0;
      const rate = rateIdx !== -1
        ? getExcelCellNumericValue(worksheet, i, rateIdx, row[rateIdx])
        : (product.Rate || 0);
      const mrp = mrpIdx !== -1
        ? getExcelCellNumericValue(worksheet, i, mrpIdx, row[mrpIdx])
        : (product.MRP || 0);

      let discountPercentage = '';
      let discountAmount = '';
      if (discPctIdx !== -1 && row[discPctIdx] !== '') {
        discountPercentage = String(getExcelCellNumericValue(worksheet, i, discPctIdx, row[discPctIdx]));
      } else if (discAmtIdx !== -1 && row[discAmtIdx] !== '') {
        discountAmount = String(getExcelCellNumericValue(worksheet, i, discAmtIdx, row[discAmtIdx]));
      }

      let gstRate = product.gstRate;
      const gstMapping = mappings.gstRate;
      const isGstRateMapped = gstMapping && gstMapping.trim() !== '' && gstMapping !== '-';
      if (isGstRateMapped && gstIdx !== -1 && row[gstIdx] !== '' && row[gstIdx] !== null) {
        gstRate = getExcelCellNumericValue(worksheet, i, gstIdx, row[gstIdx]);
      } else {
        let cgstValue = 0;
        let sgstValue = 0;
        if (cgstIdx !== -1 && row[cgstIdx] !== '' && row[cgstIdx] !== null) {
          cgstValue = getExcelCellNumericValue(worksheet, i, cgstIdx, row[cgstIdx]);
        }
        if (sgstIdx !== -1 && row[sgstIdx] !== '' && row[sgstIdx] !== null) {
          sgstValue = getExcelCellNumericValue(worksheet, i, sgstIdx, row[sgstIdx]);
        }
        if (cgstValue > 0 || sgstValue > 0) {
          gstRate = cgstValue + sgstValue;
        }
      }

      const hsnCode = parseHsnCodeFromCell(
        hsnIdx !== -1 ? row[hsnIdx] : (product.HSNCode || product.hsnCode || '')
      );
      const packageSize = parsePackingQuantity(packing, 1);

      const newProd = {
        id: product.id,
        product: { value: product.id, label: product.Item_Name || product.ItemName },
        name: product.Item_Name || product.ItemName,
        packing,
        packageSize,
        unit: product.Unit || product.unit || '',
        gstRate: isNaN(gstRate) ? 0 : gstRate,
        taxCode: product.TaxCode || '',
        rate,
        mrp,
        hsnCode,
        batch,
        expiry,
        expiryDisplay,
        qty: qty.toString(),
        foc: foc.toString(),
        discountAmount,
        discountPercentage,
      };

      newProducts.push(recalculateProductRow(newProd, summaryDiscountPct));
    }

    return { newProducts, unmappedNames };
  }, [summaryDiscountPercentage]);

  const buildProductRowsFromPdfLineItems = useCallback((lineItems, productMap, allProducts) => {
    if (!Array.isArray(lineItems) || lineItems.length === 0) {
      return { newProducts: [], unmappedNames: [] };
    }

    const summaryDiscountPct = parseFloat(summaryDiscountPercentage) || 0;
    const newProducts = [];
    const unmappedNames = [];
    const seenUnmapped = new Set();

    const supplierNormIndex = buildSupplierProductMapNormIndex(productMap);
    const productByNormName = buildProductByNormalizedNameIndex(allProducts);

    for (const item of lineItems) {
      const supplierProductName = item?.productName?.toString().trim();
      if (!supplierProductName) continue;

      let internalProductName = resolveInternalNameFromSupplierMap(
        productMap,
        supplierNormIndex,
        supplierProductName
      );
      if (!internalProductName) {
        const nameMatchProduct = productByNormName.get(normalizeNameForMatch(supplierProductName));
        if (nameMatchProduct) {
          internalProductName = String(
            nameMatchProduct.Item_Name ?? nameMatchProduct.ItemName ?? ''
          ).trim();
        }
      }
      if (!internalProductName) {
        if (!seenUnmapped.has(supplierProductName)) {
          seenUnmapped.add(supplierProductName);
          unmappedNames.push(supplierProductName);
        }
        continue;
      }

      const product = productByNormName.get(normalizeNameForMatch(internalProductName));
      if (!product) {
        if (!seenUnmapped.has(supplierProductName)) {
          seenUnmapped.add(supplierProductName);
          unmappedNames.push(supplierProductName);
        }
        continue;
      }

      const batch = item?.batch?.toString() || '';

      let expiry = '';
      let expiryDisplay = '';
      if (item?.expiry) {
        const parsedDate = parseExcelDate(item.expiry);
        if (parsedDate) {
          expiry = formatDateString(parsedDate);
          expiryDisplay = formatExpiryDisplay(parsedDate);
        }
      }

      const packing = normalizePackingFromBill(
        item?.packing,
        product.Package || product.package || product.packing || '1'
      );

      const qty = Number(item?.qty) || 0;
      const foc = Number(item?.foc) || 0;
      const rate = item?.rate != null && item?.rate !== ''
        ? Number(item.rate)
        : (product.Rate || 0);
      const mrp = item?.mrp != null && item?.mrp !== ''
        ? Number(item.mrp)
        : (product.MRP || 0);

      let discountPercentage = '';
      let discountAmount = '';
      if (item?.discountPct != null && item.discountPct !== '') {
        discountPercentage = String(item.discountPct);
      } else if (item?.discount != null && item.discount !== '') {
        discountAmount = String(item.discount);
      }

      let gstRate = product.gstRate;
      if (item?.gstRate != null && item.gstRate !== '') {
        gstRate = Number(item.gstRate);
      }

      const hsnCode = parseHsnCodeFromCell(
        item?.hsnCode ?? product.HSNCode ?? product.hsnCode ?? ''
      );
      const packageSize = parsePackingQuantity(packing, 1);

      const newProd = {
        id: product.id,
        product: { value: product.id, label: product.Item_Name || product.ItemName },
        name: product.Item_Name || product.ItemName,
        packing,
        packageSize,
        unit: product.Unit || product.unit || '',
        gstRate: isNaN(gstRate) ? 0 : gstRate,
        taxCode: product.TaxCode || '',
        rate,
        mrp,
        hsnCode,
        batch,
        expiry,
        expiryDisplay,
        qty: qty.toString(),
        foc: foc.toString(),
        discountAmount,
        discountPercentage,
      };

      newProducts.push(recalculateProductRow(newProd, summaryDiscountPct));
    }

    return { newProducts, unmappedNames };
  }, [summaryDiscountPercentage]);

  const loadUnmappedProductOptions = useCallback(async (inputValue) => {
    if (!inputValue || inputValue.trim().length < 2) return [];

    try {
      const response = await axios.get(`${BASE_URL}${GET_ITEM_MASTER}`, {
        headers: {
          ...defaultHeaders,
          PageNo: 1,
          PageSize: 1000000,
          FilterText: inputValue.trim(),
          FilterColumn: '0',
          FilterValue: '0',
        },
      });

      if (response.status === 200) {
        const masterData = response.data?.data?.masterData || [];
        const pharmacyItems = masterData.filter((item) => (item.ItemType ?? item.itemType) === 'Pharmacy');
        const normalizedProducts = normalizeProductData(pharmacyItems);
        return normalizedProducts.map((product) => ({
          value: product.id,
          label: product.Item_Name ?? product.ItemName ?? '',
          ...product,
        }));
      }
      return [];
    } catch (error) {
      console.error('Failed to fetch products for unmapped mapping:', error);
      return [];
    }
  }, [defaultHeaders]);

  const resetProductRowRefs = (count) => {
    setTimeout(() => {
      batchRefs.current = Array(count).fill(null);
      expiryRefs.current = Array(count).fill(null);
      packingRefs.current = Array(count).fill(null);
      mrpRefs.current = Array(count).fill(null);
      qtyRefs.current = Array(count).fill(null);
      focRefs.current = Array(count).fill(null);
      rateRefs.current = Array(count).fill(null);
      discountRefs.current = Array(count).fill(null);
      discountPercentageRefs.current = Array(count).fill(null);
      gstRateRefs.current = Array(count).fill(null);
      hsnCodeRefs.current = Array(count).fill(null);
    }, 100);
  };

  const fetchAllPharmacyProducts = async () => {
    try {
      const response = await axios.get(`${BASE_URL}${GET_ITEM_MASTER}`, {
        headers: {
          ...defaultHeaders,
          PageNo: 1,
          PageSize: 1000000,
          FilterText: '',
          FilterColumn: '0',
          FilterValue: '0',
        },
      });
      if (response.status === 200) {
        const masterData = response.data?.data?.masterData || [];
        const pharmacyItems = masterData.filter((item) => (item.ItemType ?? item.itemType) === 'Pharmacy');
        return normalizeProductData(pharmacyItems);
      }
    } catch (err) {
      console.error('Failed to fetch products for mapping', err);
      toast.error('Failed to fetch product list. Matching may fail.');
    }
    return [];
  };

  const applyUnmappedUploadContext = (unmappedNames, uploadContext, allProducts) => {
    const prefill = buildUnmappedExactNamePrefill(unmappedNames, allProducts);
    setPendingExcelUploadContext(uploadContext);
    setUnmappedSupplierProducts(unmappedNames);
    setSelectedUnmappedProductMap(prefill);
    setUnmappedItemsToCreate([]);
    setShowSupplierProductMappingModal(true);
    const prefillCount = Object.keys(prefill).length;
    toast.info(
      prefillCount > 0
        ? `Found ${unmappedNames.length} unmapped product name(s). ${prefillCount} matched item master and were pre-selected.`
        : `Found ${unmappedNames.length} unmapped product name(s)`,
      { autoClose: 3500 }
    );
  };

  const handleExcelUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!selectedSupplier) {
      toast.error('Please select a supplier first', { autoClose: 2000 });
      event.target.value = '';
      return;
    }

    // Check if mappings exist
    const mappings = getSupplierColumnMappings(selectedSupplier);
    if (!mappings || Object.keys(mappings).length === 0) {
      toast.error('No column mappings found for this supplier', { autoClose: 2000 });
      event.target.value = '';
      return;
    }

    // Check if product mappings exist
    const productMap = getSupplierProductMappings(selectedSupplier);
    if (!productMap || Object.keys(productMap).length === 0) {
      toast.warning('No product name mappings found. Products might not be identified.', { autoClose: 3000 });
    }

    setLoading(true);

    try {
      const allProducts = await fetchAllPharmacyProducts();

      // 2. Read Excel File
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

      if (jsonData.length < 2) {
        toast.error('Excel file appears undefined or empty', { autoClose: 2000 });
        setLoading(false);
        event.target.value = '';
        return;
      }

      const { newProducts, unmappedNames } = buildProductRowsFromExcel(jsonData, mappings, productMap, allProducts, worksheet);

      if (newProducts.length > 0) {
        setProducts(newProducts);
        toast.success(`Loaded ${newProducts.length} mapped item(s) from Excel`);
        resetProductRowRefs(newProducts.length);
      }

      if (unmappedNames.length > 0) {
        applyUnmappedUploadContext(unmappedNames, {
          source: 'excel',
          jsonData,
          mappings,
          allProducts,
          worksheet,
        }, allProducts);
      } else if (newProducts.length === 0) {
        toast.info('No mapped products found in the Excel file');
      }

    } catch (error) {
      console.error('Excel upload error:', error);
      toast.error('Failed to process Excel file');
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  const handlePdfUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!selectedSupplier) {
      toast.error('Please select a supplier first', { autoClose: 2000 });
      event.target.value = '';
      return;
    }

    const clientId = await resolveClientId();
    if (!clientId) {
      toast.error('Client ID not found. Please log in again.', { autoClose: 2500 });
      event.target.value = '';
      return;
    }

    const supplierId =
      selectedSupplier?.SupplierID ??
      selectedSupplier?.supplierID ??
      selectedSupplier?.value ??
      selectedSupplier?.SupplierId;

    if (!supplierId) {
      toast.error('Supplier ID not found', { autoClose: 2000 });
      event.target.value = '';
      return;
    }

    const productMap = getSupplierProductMappings(selectedSupplier);
    if (!productMap || Object.keys(productMap).length === 0) {
      toast.warning('No product name mappings found. Products might not be identified.', { autoClose: 3000 });
    }

    setAiExtracting(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('supplierId', String(supplierId));
      formData.append('clientId', String(clientId));

      const response = await axios.post(
        `${AI_EXTRACT_BASE_URL}${EXTRACT_PURCHASE_BILL}`,
        formData,
        {
          headers: {
            'X-Client-ID': AI_EXTRACT_CLIENT_ID,
            'X-Service-Key': AI_EXTRACT_SERVICE_KEY,
          },
        }
      );

      const extracted = response.data;
      const lineItems = extracted?.lineItems || [];

      if (!lineItems.length) {
        toast.error('No line items found in the PDF', { autoClose: 2500 });
        return;
      }

      if (extracted?.header) {
        const { invoiceNo, invoiceDate: extractedInvoiceDate } = extracted.header;
        if (invoiceNo) {
          setInvoiceNumber(String(invoiceNo).trim().toUpperCase());
        }
        if (extractedInvoiceDate) {
          const formattedDate = formatDateString(extractedInvoiceDate);
          if (formattedDate) {
            setInvoiceDate(formattedDate);
          }
        }
      }

      const allProducts = await fetchAllPharmacyProducts();
      const { newProducts, unmappedNames } = buildProductRowsFromPdfLineItems(
        lineItems,
        productMap,
        allProducts
      );

      if (newProducts.length > 0) {
        setProducts(newProducts);
        toast.success(`Loaded ${newProducts.length} mapped item(s) from PDF`);
        resetProductRowRefs(newProducts.length);
      }

      if (unmappedNames.length > 0) {
        applyUnmappedUploadContext(unmappedNames, {
          source: 'pdf',
          lineItems,
          allProducts,
        }, allProducts);
      } else if (newProducts.length === 0) {
        toast.info('No mapped products found in the PDF');
      }
    } catch (error) {
      console.error('PDF upload error:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        error.message ||
        'Failed to extract purchase bill from PDF';
      toast.error(errorMessage, { autoClose: 3000 });
    } finally {
      setAiExtracting(false);
      event.target.value = '';
    }
  };

  const handleUnmappedProductSelection = (supplierProductName, selectedOption) => {
    setSelectedUnmappedProductMap((prev) => ({
      ...prev,
      [supplierProductName]: selectedOption || null,
    }));
  };

  const removeUnmappedSupplierProduct = (supplierProductName) => {
    setUnmappedSupplierProducts((prev) => prev.filter((name) => name !== supplierProductName));
    setSelectedUnmappedProductMap((prev) => {
      const next = { ...prev };
      delete next[supplierProductName];
      return next;
    });
    setUnmappedItemsToCreate((prev) => prev.filter((name) => name !== supplierProductName));
  };

  const toggleUnmappedItemForCreation = (supplierProductName) => {
    setUnmappedItemsToCreate((prev) =>
      prev.includes(supplierProductName)
        ? prev.filter((name) => name !== supplierProductName)
        : [...prev, supplierProductName]
    );
  };

  const toggleAllUnmappedItemsForCreation = () => {
    setUnmappedItemsToCreate((prev) =>
      prev.length === unmappedSupplierProducts.length ? [] : [...unmappedSupplierProducts]
    );
  };

  /** Rows that still have no internal product chosen — the ones that actually need creating */
  const unmatchedUnmappedProducts = useMemo(
    () => unmappedSupplierProducts.filter((name) => !selectedUnmappedProductMap[name]?.label),
    [unmappedSupplierProducts, selectedUnmappedProductMap]
  );

  const selectOnlyUnmatchedUnmappedItems = () => {
    if (unmatchedUnmappedProducts.length === 0) {
      toast.info('All items already have an internal product selected', { autoClose: 2000 });
      return;
    }
    setUnmappedItemsToCreate([...unmatchedUnmappedProducts]);
  };

  /** Create ticked supplier product names directly in item master (code = name), then auto-select them */
  const handleCreateItemsFromUnmapped = async () => {
    const namesToCreate = unmappedSupplierProducts.filter((name) => unmappedItemsToCreate.includes(name));

    if (namesToCreate.length === 0) {
      toast.error('Please tick at least one item to create', { autoClose: 2000 });
      return;
    }

    setIsCreatingUnmappedItems(true);
    const createdNames = [];
    const failedNames = [];

    try {
      for (const supplierProductName of namesToCreate) {
        try {
          const response = await axios.post(
            `${BASE_URL}${POST_ITEM_MASTER}`,
            buildDefaultItemMasterPayload(supplierProductName),
            { headers: defaultHeaders }
          );

          if (response.status === 200) {
            createdNames.push(supplierProductName);
          } else {
            failedNames.push(supplierProductName);
          }
        } catch (error) {
          console.error(`Failed to create item "${supplierProductName}":`, error);
          failedNames.push(supplierProductName);
        }
      }

      if (createdNames.length > 0) {
        // Refresh item master so the new items are selectable and row rebuild can find them
        const refreshedProducts = await fetchAllPharmacyProducts();
        const productByNormName = buildProductByNormalizedNameIndex(refreshedProducts);

        setSelectedUnmappedProductMap((prev) => {
          const next = { ...prev };
          createdNames.forEach((supplierProductName) => {
            const product = productByNormName.get(normalizeNameForMatch(supplierProductName));
            if (product) {
              next[supplierProductName] = masterProductToUnmappedSelectOption(product);
            }
          });
          return next;
        });

        if (refreshedProducts.length > 0) {
          setPendingExcelUploadContext((prev) =>
            prev ? { ...prev, allProducts: refreshedProducts } : prev
          );
        }

        setUnmappedItemsToCreate((prev) => prev.filter((name) => !createdNames.includes(name)));
        toast.success(`Created ${createdNames.length} item(s) in item master`, { autoClose: 2000 });
      }

      if (failedNames.length > 0) {
        toast.error(`Failed to create ${failedNames.length} item(s)`, { autoClose: 2500 });
      }
    } finally {
      setIsCreatingUnmappedItems(false);
    }
  };

  /**
   * Re-pull item master and re-match rows that still have no internal product,
   * so newly created items show up without closing/reopening the modal.
   */
  const handleReloadUnmappedMappings = async () => {
    if (unmappedSupplierProducts.length === 0) return;

    setIsReloadingUnmappedMatches(true);
    try {
      const refreshedProducts = await fetchAllPharmacyProducts();
      if (refreshedProducts.length === 0) {
        toast.error('Could not reload item master', { autoClose: 2000 });
        return;
      }

      const productByNormName = buildProductByNormalizedNameIndex(refreshedProducts);
      const productMap = getSupplierProductMappings(selectedSupplier);
      const supplierNormIndex = buildSupplierProductMapNormIndex(productMap);

      const newlyMatched = [];
      const newSelections = {};
      unmappedSupplierProducts.forEach((supplierProductName) => {
        if (selectedUnmappedProductMap[supplierProductName]?.label) return;

        const internalName = resolveInternalNameFromSupplierMap(
          productMap,
          supplierNormIndex,
          supplierProductName
        );
        const product =
          (internalName && productByNormName.get(normalizeNameForMatch(internalName))) ||
          productByNormName.get(normalizeNameForMatch(supplierProductName));

        if (product) {
          newSelections[supplierProductName] = masterProductToUnmappedSelectOption(product);
          newlyMatched.push(supplierProductName);
        }
      });

      if (newlyMatched.length > 0) {
        setSelectedUnmappedProductMap((prev) => ({ ...prev, ...newSelections }));
      }

      setPendingExcelUploadContext((prev) =>
        prev ? { ...prev, allProducts: refreshedProducts } : prev
      );
      setUnmappedItemsToCreate((prev) => prev.filter((name) => !newlyMatched.includes(name)));

      toast.success(
        newlyMatched.length > 0
          ? `Reloaded item master. ${newlyMatched.length} item(s) matched and selected.`
          : 'Reloaded item master. No new matches found.',
        { autoClose: 2500 }
      );
    } catch (error) {
      console.error('Failed to reload unmapped product matches:', error);
      toast.error('Failed to reload mappings', { autoClose: 2000 });
    } finally {
      setIsReloadingUnmappedMatches(false);
    }
  };

  const clearSupplierProductMappingContext = () => {
    setPendingExcelUploadContext(null);
    setUnmappedSupplierProducts([]);
    setSelectedUnmappedProductMap({});
    setUnmappedItemsToCreate([]);
    setIsSavingSupplierProductMappings(false);
    setIsCreatingUnmappedItems(false);
    setIsReloadingUnmappedMatches(false);
  };

  const handleCloseSupplierProductMappingModal = ({ clearContext = false } = {}) => {
    setShowSupplierProductMappingModal(false);
    if (clearContext) {
      clearSupplierProductMappingContext();
    }
  };

  const handleSaveSupplierProductMappings = async () => {
    if (!selectedSupplier || !pendingExcelUploadContext) return;

    const unmappedWithoutSelection = unmappedSupplierProducts.filter(
      (name) => !selectedUnmappedProductMap[name]?.label
    );

    if (unmappedWithoutSelection.length > 0) {
      toast.error('Please choose internal product for all unmapped items', { autoClose: 2000 });
      return;
    }

    const existingMappings = getSupplierColumnMappings(selectedSupplier);
    const existingProductMap = getSupplierProductMappings(selectedSupplier);
    const mergedProductMap = { ...existingProductMap };

    unmappedSupplierProducts.forEach((supplierProductName) => {
      const selectedOption = selectedUnmappedProductMap[supplierProductName];
      if (selectedOption?.label) {
        mergedProductMap[supplierProductName] = selectedOption.label;
      }
    });

    const payload = {
      SupplierID: selectedSupplier.value,
      SupplierName: selectedSupplier.label,
      Mappings: existingMappings,
      Products: mergedProductMap,
    };

    setIsSavingSupplierProductMappings(true);
    try {
      const response = await axios.post(`${BASE_URL}${POST_SUPPLIER_MAPPING}`, payload, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`
        }
      });

      if (!(response.status === 200 && response.data?.isSucess)) {
        throw new Error(response.data?.message || 'Failed to save product mappings');
      }

      const updatedSupplier = {
        ...selectedSupplier,
        Mappings: {
          columns: existingMappings,
          products: mergedProductMap,
        },
        Products: mergedProductMap,
      };

      setSuppliers((prevSuppliers) =>
        (prevSuppliers || []).map((supplier) => {
          const supplierId =
            supplier.SupplierID ??
            supplier.supplierID ??
            supplier.Id ??
            supplier.id ??
            supplier.SupplierId;
          if (supplierId === selectedSupplier.value) {
            return {
              ...supplier,
              Mappings: {
                columns: existingMappings,
                products: mergedProductMap,
              },
              Products: mergedProductMap,
            };
          }
          return supplier;
        })
      );
      setSelectedSupplier(updatedSupplier);

      const { newProducts } = pendingExcelUploadContext.source === 'pdf'
        ? buildProductRowsFromPdfLineItems(
          pendingExcelUploadContext.lineItems,
          mergedProductMap,
          pendingExcelUploadContext.allProducts
        )
        : buildProductRowsFromExcel(
          pendingExcelUploadContext.jsonData,
          pendingExcelUploadContext.mappings,
          mergedProductMap,
          pendingExcelUploadContext.allProducts,
          pendingExcelUploadContext.worksheet
        );

      if (newProducts.length > 0) {
        setProducts(newProducts);
        resetProductRowRefs(newProducts.length);
        toast.success(`Mapped and loaded ${newProducts.length} item(s)`);
      } else {
        toast.info(
          pendingExcelUploadContext.source === 'pdf'
            ? 'Mappings saved, but no rows could be loaded from PDF'
            : 'Mappings saved, but no rows could be loaded from Excel'
        );
      }

      handleCloseSupplierProductMappingModal({ clearContext: true });
    } catch (error) {
      console.error('Failed to save supplier product mappings:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.Messages ||
        error.message ||
        'Failed to save mappings';
      toast.error(errorMessage, { autoClose: 2000 });
    } finally {
      setIsSavingSupplierProductMappings(false);
    }
  };

  const updateProductRow = (index, updater, { skipRecalculate = false } = {}) => {
    const summaryDiscountPct = parseFloat(summaryDiscountPercentage) || 0;
    setProducts((prevProducts) =>
      prevProducts.map((product, i) => {
        if (i !== index) return product;
        const updatedProduct = updater(product);
        return skipRecalculate ? updatedProduct : recalculateProductRow(updatedProduct, summaryDiscountPct);
      })
    );
  };

  const handleQtyChange = (index, newQty) => {
    updateProductRow(index, (product) => ({
      ...product,
      qty: newQty,
    }));
  };

  const handleFOCChange = (index, newFoc) => {
    updateProductRow(index, (product) => ({
      ...product,
      foc: newFoc,
    }));
  };

  const handleRateChange = (index, newRate) => {
    const rateNum = parseFloat(newRate);
    const rateIsZero = newRate !== '' && !isNaN(rateNum) && rateNum <= 0;

    updateProductRow(index, (product) => ({
      ...product,
      rate: newRate,
      ...(rateIsZero
        ? { discountAmount: '', discountPercentage: '' }
        : {}),
    }));
  };

  const handleDiscountAmountChange = (index, newDiscountAmount) => {
    const product = products[index];
    if (!product) return;

    const rate = parseFloat(product.rate || 0);
    if (rate <= 0) {
      toast.error('Discount not allowed when rate is 0', { autoClose: 1500 });
      return;
    }

    const trimmed = newDiscountAmount == null ? '' : String(newDiscountAmount);
    if (trimmed === '' || trimmed === '.') {
      updateProductRow(index, (p) => ({
        ...p,
        discountAmount: trimmed,
      }));
      return;
    }

    if (trimmed.startsWith('-') || parseFloat(trimmed) < 0) {
      toast.error('Discount cannot be negative', { autoClose: 1500 });
      return;
    }

    const qty = parseFloat(product.qty || 0);
    const itemTotal = qty * rate;
    const num = parseFloat(trimmed);

    if (!isNaN(num) && num > itemTotal) {
      toast.warning(`Discount cannot exceed item total (${itemTotal.toFixed(2)})`, { autoClose: 1500 });
      const capped = Number.isInteger(itemTotal) ? String(itemTotal) : itemTotal.toFixed(2);
      updateProductRow(index, (p) => ({
        ...p,
        discountAmount: capped,
      }));
      return;
    }

    updateProductRow(index, (p) => ({
      ...p,
      discountAmount: trimmed,
    }));
  };

  const handleDiscountPercentageChange = (index, newDiscountPercentage) => {
    const product = products[index];
    if (!product) return;

    const rate = parseFloat(product.rate || 0);
    if (rate <= 0) {
      toast.error('Discount not allowed when rate is 0', { autoClose: 1500 });
      return;
    }

    const trimmed = newDiscountPercentage == null ? '' : String(newDiscountPercentage);
    if (trimmed === '' || trimmed === '.') {
      updateProductRow(index, (p) => ({
        ...p,
        discountPercentage: trimmed,
      }));
      return;
    }

    if (trimmed.startsWith('-') || parseFloat(trimmed) < 0) {
      toast.error('Discount cannot be negative', { autoClose: 1500 });
      return;
    }

    const num = parseFloat(trimmed);
    if (!isNaN(num) && num > 100) {
      toast.warning('Discount cannot exceed 100%', { autoClose: 1500 });
      updateProductRow(index, (p) => ({
        ...p,
        discountPercentage: '100',
      }));
      return;
    }

    updateProductRow(index, (p) => ({
      ...p,
      discountPercentage: trimmed,
    }));
  };

  const handleSummaryDiscountAmountChange = (newValue) => {
    const trimmed = newValue == null ? '' : String(newValue);
    if (trimmed === '' || trimmed === '.') {
      setSummaryDiscountAmount(trimmed);
      return;
    }

    if (trimmed.startsWith('-') || parseFloat(trimmed) < 0) {
      toast.error('Discount cannot be negative', { autoClose: 1500 });
      return;
    }

    const invoiceTotal = products.reduce(
      (sum, product) => sum + parseFloat(product.totalAmount || 0),
      0
    );
    const num = parseFloat(trimmed);

    if (!isNaN(num) && num > invoiceTotal) {
      toast.warning(
        `Discount cannot exceed invoice total (${invoiceTotal.toFixed(2)})`,
        { autoClose: 1500 }
      );
      const capped = Number.isInteger(invoiceTotal)
        ? String(invoiceTotal)
        : invoiceTotal.toFixed(2);
      setSummaryDiscountAmount(capped);
      setSummaryDiscountPercentage('');
      return;
    }

    setSummaryDiscountAmount(trimmed);
    if (trimmed) {
      setSummaryDiscountPercentage('');
    }
  };

  const handleSummaryDiscountPercentageChange = (newValue) => {
    const trimmed = newValue == null ? '' : String(newValue);
    if (trimmed === '' || trimmed === '.') {
      setSummaryDiscountPercentage(trimmed);
      return;
    }

    if (trimmed.startsWith('-') || parseFloat(trimmed) < 0) {
      toast.error('Discount cannot be negative', { autoClose: 1500 });
      return;
    }

    const num = parseFloat(trimmed);
    if (!isNaN(num) && num > 100) {
      toast.warning('Discount cannot exceed 100%', { autoClose: 1500 });
      setSummaryDiscountPercentage('100');
      setSummaryDiscountAmount('');
      return;
    }

    setSummaryDiscountPercentage(trimmed);
    if (trimmed) {
      setSummaryDiscountAmount('');
    }
  };

  const handlePackingChange = (index, newPacking) => {
    updateProductRow(index, (product) => ({
      ...product,
      packing: newPacking,
      packageSize: parsePackingQuantity(newPacking, product.packageSize || 1),
    }));
  };

  const handleBatchChange = (index, newBatch) => {
    updateProductRow(
      index,
      (product) => ({
        ...product,
        batch: newBatch,
      }),
      { skipRecalculate: true }
    );
  };


  // Handle expiry date input with MM/YY format
  const handleExpiryInputChange = (index, value) => {
    // Remove any non-numeric characters
    const numericValue = value.replace(/\D/g, '');

    let formattedValue = '';

    if (numericValue.length >= 1) {
      // Add first digit
      formattedValue = numericValue[0];
    }
    if (numericValue.length >= 2) {
      // Add second digit with slash
      formattedValue = numericValue[0] + numericValue[1];
    }
    if (numericValue.length >= 3) {
      // Add third digit
      formattedValue = numericValue[0] + numericValue[1] + '/' + numericValue[2];
    }
    if (numericValue.length >= 4) {
      // Add fourth digit
      formattedValue = numericValue[0] + numericValue[1] + '/' + numericValue[2] + numericValue[3];
    }

    // Convert to full date format for storage (assuming 20xx for years)
    let fullDate = '';
    if (numericValue.length === 4) {
      const month = numericValue.substring(0, 2);
      const year = '20' + numericValue.substring(2, 4);
      fullDate = `${year}-${month}-01`; // Use first day of month
      const parsedDate = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1);
      if (!Number.isNaN(parsedDate.getTime())) {
        formattedValue = formatExpiryDisplay(parsedDate);
      }
    }

    const updatedProducts = products.map((product, i) => {
      if (i === index) {
        return {
          ...product,
          expiry: fullDate,
          expiryDisplay: formattedValue
        };
      }
      return product;
    });
    setProducts(updatedProducts);
  };

  // Handle MRP changes
  const handleMRPChange = (index, newMRP) => {
    updateProductRow(index, (product) => ({
      ...product,
      mrp: newMRP,
    }));
  };

  // Handle GST rate changes
  const handleGSTRateChange = (index, newGSTRate) => {
    updateProductRow(index, (product) => ({
      ...product,
      gstRate: newGSTRate === '' ? '' : parseFloat(newGSTRate),
    }));
  };

  // Handle HSN Code changes
  const handleHSNCodeChange = (index, newHSNCode) => {
    updateProductRow(
      index,
      (product) => ({
        ...product,
        hsnCode: newHSNCode,
      }),
      { skipRecalculate: true }
    );
  };

  // Handle batch input key navigation
  const handleBatchKeyDown = (e, index) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      // Focus previous row's batch input
      if (index > 0 && batchRefs.current[index - 1]) {
        batchRefs.current[index - 1].focus();
        batchRefs.current[index - 1].select();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      // Focus next row's batch input
      if (index < products.length - 1 && batchRefs.current[index + 1]) {
        batchRefs.current[index + 1].focus();
        batchRefs.current[index + 1].select();
      } else if (index === products.length - 1) {
        // If on last row, focus the product search input
        setTimeout(() => {
          if (productSearchInputRef.current) {
            productSearchInputRef.current.focus();
            setShowProductList(true);
            setSelectedProductIndex(0);
            // Calculate position for dropdown
            if (productSearchContainerRef.current) {
              const rect = productSearchContainerRef.current.getBoundingClientRect();
              setDropdownPosition(calculateDropdownPosition(rect));
            }
          }
        }, 150);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (expiryRefs.current[index]) {
        expiryRefs.current[index].focus();
        expiryRefs.current[index].select();
      }
    }
  };

  // Handle expiry input key navigation
  const handleExpiryKeyDown = (e, index) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      // Focus previous row's expiry input
      if (index > 0 && expiryRefs.current[index - 1]) {
        expiryRefs.current[index - 1].focus();
        expiryRefs.current[index - 1].select();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      // Focus next row's expiry input
      if (index < products.length - 1 && expiryRefs.current[index + 1]) {
        expiryRefs.current[index + 1].focus();
        expiryRefs.current[index + 1].select();
      } else if (index === products.length - 1) {
        // If on last row, focus the product search input
        setTimeout(() => {
          if (productSearchInputRef.current) {
            productSearchInputRef.current.focus();
            setShowProductList(true);
            setSelectedProductIndex(0);
            // Calculate position for dropdown
            if (productSearchContainerRef.current) {
              const rect = productSearchContainerRef.current.getBoundingClientRect();
              setDropdownPosition(calculateDropdownPosition(rect));
            }
          }
        }, 150);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const product = products[index];
      const parsedExpiry = parseExpiryMonthYear(product?.expiryDisplay, product?.expiry);

      if (parsedExpiry) {
        const { month, year } = parsedExpiry;

        const now = new Date();
        const currentMonth = now.getMonth() + 1;
        const currentYear = now.getFullYear();

        const isValidMonth = month >= 1 && month <= 12;
        const isPastMonth = year < currentYear || (year === currentYear && month < currentMonth);

        if (!isValidMonth || isPastMonth) {
          toast.error('Expiry must be current month or a future month.', { autoClose: 1500 });
          if (expiryRefs.current[index]) {
            expiryRefs.current[index].focus();
            expiryRefs.current[index].select();
          }
          return;
        }
      }

      if (qtyRefs.current[index]) {
        qtyRefs.current[index].focus();
        qtyRefs.current[index].select();
      }
    }
  };

  // Handle packing input key navigation
  const handlePackingKeyDown = (e, index) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      // Focus previous row's packing input
      if (index > 0 && packingRefs.current[index - 1]) {
        packingRefs.current[index - 1].focus();
        packingRefs.current[index - 1].select();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      // Focus next row's packing input
      if (index < products.length - 1 && packingRefs.current[index + 1]) {
        packingRefs.current[index + 1].focus();
        packingRefs.current[index + 1].select();
      } else if (index === products.length - 1) {
        // If on last row, focus the product search input
        setTimeout(() => {
          if (productSearchInputRef.current) {
            productSearchInputRef.current.focus();
            setShowProductList(true);
            setSelectedProductIndex(0);
            // Calculate position for dropdown
            if (productSearchContainerRef.current) {
              const rect = productSearchContainerRef.current.getBoundingClientRect();
              setDropdownPosition(calculateDropdownPosition(rect));
            }
          }
        }, 150);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (batchRefs.current[index]) {
        batchRefs.current[index].focus();
        batchRefs.current[index].select();
      }
    }
  };

  // Handle quantity input key navigation
  const handleQtyKeyDown = (e, index) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      // Focus previous row's qty input
      if (index > 0 && qtyRefs.current[index - 1]) {
        qtyRefs.current[index - 1].focus();
        qtyRefs.current[index - 1].select();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      // Focus next row's qty input
      if (index < products.length - 1 && qtyRefs.current[index + 1]) {
        qtyRefs.current[index + 1].focus();
        qtyRefs.current[index + 1].select();
      } else if (index === products.length - 1) {
        // If on last row, focus the product search input
        setTimeout(() => {
          if (productSearchInputRef.current) {
            productSearchInputRef.current.focus();
            setShowProductList(true);
            setSelectedProductIndex(0);
            // Calculate position for dropdown
            if (productSearchContainerRef.current) {
              const rect = productSearchContainerRef.current.getBoundingClientRect();
              setDropdownPosition(calculateDropdownPosition(rect));
            }
          }
        }, 150);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (focRefs.current[index]) {
        focRefs.current[index].focus();
        focRefs.current[index].select();
      }
    }
  };

  // Handle MRP input key navigation
  const handleMRPKeyDown = (e, index) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      // Focus previous row's mrp input
      if (index > 0 && mrpRefs.current[index - 1]) {
        mrpRefs.current[index - 1].focus();
        mrpRefs.current[index - 1].select();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      // Focus next row's mrp input
      if (index < products.length - 1 && mrpRefs.current[index + 1]) {
        mrpRefs.current[index + 1].focus();
        mrpRefs.current[index + 1].select();
      } else if (index === products.length - 1) {
        // If on last row, focus the product search input
        setTimeout(() => {
          if (productSearchInputRef.current) {
            productSearchInputRef.current.focus();
            setShowProductList(true);
            setSelectedProductIndex(0);
            // Calculate position for dropdown
            if (productSearchContainerRef.current) {
              const rect = productSearchContainerRef.current.getBoundingClientRect();
              setDropdownPosition(calculateDropdownPosition(rect));
            }
          }
        }, 150);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Focus the product search input in the yellow row and show the list
      setTimeout(() => {
        if (productSearchInputRef.current) {
          productSearchInputRef.current.focus();
          setShowProductList(true);
          setSelectedProductIndex(0);
          // Calculate position for dropdown
          if (productSearchContainerRef.current) {
            const rect = productSearchContainerRef.current.getBoundingClientRect();
            setDropdownPosition(calculateDropdownPosition(rect));
          }
        }
      }, 150);
    }
  };

  const handleFOCKeyDown = (e, index) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      // Focus previous row's foc input
      if (index > 0 && focRefs.current[index - 1]) {
        focRefs.current[index - 1].focus();
        focRefs.current[index - 1].select();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      // Focus next row's foc input
      if (index < products.length - 1 && focRefs.current[index + 1]) {
        focRefs.current[index + 1].focus();
        focRefs.current[index + 1].select();
      } else if (index === products.length - 1) {
        // If on last row, focus the product search input
        setTimeout(() => {
          if (productSearchInputRef.current) {
            productSearchInputRef.current.focus();
            setShowProductList(true);
            setSelectedProductIndex(0);
            // Calculate position for dropdown
            if (productSearchContainerRef.current) {
              const rect = productSearchContainerRef.current.getBoundingClientRect();
              setDropdownPosition(calculateDropdownPosition(rect));
            }
          }
        }, 150);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (rateRefs.current[index]) {
        rateRefs.current[index].focus();
        rateRefs.current[index].select();
      }
    }
  };

  // Handle rate input key navigation
  const handleRateKeyDown = (e, index) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      // Focus previous row's rate input
      if (index > 0 && rateRefs.current[index - 1]) {
        rateRefs.current[index - 1].focus();
        rateRefs.current[index - 1].select();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      // Focus next row's rate input
      if (index < products.length - 1 && rateRefs.current[index + 1]) {
        rateRefs.current[index + 1].focus();
        rateRefs.current[index + 1].select();
      } else if (index === products.length - 1) {
        // If on last row, focus the product search input
        setTimeout(() => {
          if (productSearchInputRef.current) {
            productSearchInputRef.current.focus();
            setShowProductList(true);
            setSelectedProductIndex(0);
            // Calculate position for dropdown
            if (productSearchContainerRef.current) {
              const rect = productSearchContainerRef.current.getBoundingClientRect();
              setDropdownPosition(calculateDropdownPosition(rect));
            }
          }
        }, 150);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (mrpRefs.current[index]) {
        mrpRefs.current[index].focus();
        mrpRefs.current[index].select();
      }
    }
  };

  // Handle discount input key navigation
  const handleDiscountKeyDown = (e, index) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      // Focus previous row's discount input
      if (index > 0 && discountRefs.current[index - 1]) {
        discountRefs.current[index - 1].focus();
        discountRefs.current[index - 1].select();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      // Focus next row's discount input
      if (index < products.length - 1 && discountRefs.current[index + 1]) {
        discountRefs.current[index + 1].focus();
        discountRefs.current[index + 1].select();
      } else if (index === products.length - 1) {
        // If on last row, focus the product search input
        setTimeout(() => {
          if (productSearchInputRef.current) {
            productSearchInputRef.current.focus();
            setShowProductList(true);
            setSelectedProductIndex(0);
            // Calculate position for dropdown
            if (productSearchContainerRef.current) {
              const rect = productSearchContainerRef.current.getBoundingClientRect();
              setDropdownPosition(calculateDropdownPosition(rect));
            }
          }
        }, 150);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (discountPercentageRefs.current[index]) {
        discountPercentageRefs.current[index].focus();
        discountPercentageRefs.current[index].select();
      }
    }
  };

  // Handle discount percentage input key navigation
  const handleDiscountPercentageKeyDown = (e, index) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      // Focus previous row's discount percentage input
      if (index > 0 && discountPercentageRefs.current[index - 1]) {
        discountPercentageRefs.current[index - 1].focus();
        discountPercentageRefs.current[index - 1].select();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      // Focus next row's discount percentage input
      if (index < products.length - 1 && discountPercentageRefs.current[index + 1]) {
        discountPercentageRefs.current[index + 1].focus();
        discountPercentageRefs.current[index + 1].select();
      } else if (index === products.length - 1) {
        // If on last row, focus the product search input
        setTimeout(() => {
          if (productSearchInputRef.current) {
            productSearchInputRef.current.focus();
            setShowProductList(true);
            setSelectedProductIndex(0);
            // Calculate position for dropdown
            if (productSearchContainerRef.current) {
              const rect = productSearchContainerRef.current.getBoundingClientRect();
              setDropdownPosition(calculateDropdownPosition(rect));
            }
          }
        }, 150);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (hsnCodeRefs.current[index]) {
        hsnCodeRefs.current[index].focus();
        hsnCodeRefs.current[index].select();
      }
    }
  };

  // Handle GST rate input key navigation
  const handleGSTRateKeyDown = (e, index) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      // Focus previous row's gst rate input
      if (index > 0 && gstRateRefs.current[index - 1]) {
        gstRateRefs.current[index - 1].focus();
        gstRateRefs.current[index - 1].select();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      // Focus next row's gst rate input
      if (index < products.length - 1 && gstRateRefs.current[index + 1]) {
        gstRateRefs.current[index + 1].focus();
        gstRateRefs.current[index + 1].select();
      } else if (index === products.length - 1) {
        // If on last row, focus the product search input
        setTimeout(() => {
          if (productSearchInputRef.current) {
            productSearchInputRef.current.focus();
            setShowProductList(true);
            setSelectedProductIndex(0);
            // Calculate position for dropdown
            if (productSearchContainerRef.current) {
              const rect = productSearchContainerRef.current.getBoundingClientRect();
              setDropdownPosition(calculateDropdownPosition(rect));
            }
          }
        }, 150);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (hsnCodeRefs.current[index]) {
        hsnCodeRefs.current[index].focus();
        hsnCodeRefs.current[index].select();
      }
    }
  };

  // Handle HSN Code input key navigation
  const handleHSNCodeKeyDown = (e, index) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      // Focus previous row's hsn code input
      if (index > 0 && hsnCodeRefs.current[index - 1]) {
        hsnCodeRefs.current[index - 1].focus();
        hsnCodeRefs.current[index - 1].select();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      // Focus next row's hsn code input
      if (index < products.length - 1 && hsnCodeRefs.current[index + 1]) {
        hsnCodeRefs.current[index + 1].focus();
        hsnCodeRefs.current[index + 1].select();
      } else if (index === products.length - 1) {
        // If on last row, focus the product search input
        setTimeout(() => {
          if (productSearchInputRef.current) {
            productSearchInputRef.current.focus();
            setShowProductList(true);
            setSelectedProductIndex(0);
            // Calculate position for dropdown
            if (productSearchContainerRef.current) {
              const rect = productSearchContainerRef.current.getBoundingClientRect();
              setDropdownPosition(calculateDropdownPosition(rect));
            }
          }
        }, 150);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Focus the product search input in the yellow row and show the list
      setTimeout(() => {
        if (productSearchInputRef.current) {
          productSearchInputRef.current.focus();
          setShowProductList(true);
          setSelectedProductIndex(0);
          // Calculate position for dropdown
          if (productSearchContainerRef.current) {
            const rect = productSearchContainerRef.current.getBoundingClientRect();
            setDropdownPosition(calculateDropdownPosition(rect));
          }
        }
      }, 150);
    }
  };

  // Handle product selection from search dropdown
  // Fetch products from API based on search text (similar to getInvoiceItems in SalesEntry)
  const getProducts = async (filterText) => {
    if (!filterText || filterText.length < 2) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_ITEM_MASTER}`, {
        headers: {
          ...defaultHeaders,
          PageNo: 1,
          PageSize: 1000,
          FilterText: filterText,
        },
      });

      if (response.status === 200) {
        const masterData = response.data?.data?.masterData || [];
        const pharmacyItems = masterData.filter(
          (item) => (item.ItemType ?? item.itemType) === 'Pharmacy'
        );
        setProductsMaster(normalizeProductData(pharmacyItems));
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to fetch products.', {
        autoClose: 1000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProductFromSearch = (product, targetRowIndex = null) => {
    if (!product) return;

    // Clear search and hide dropdown
    setProductSearch('');
    setShowProductList(false);
    setSelectedProductIndex(-1);
    setProductSearchForEdit('');
    setEditingProductRowIndex(null);

    // If targetRowIndex is provided, we're editing an existing row
    const isEditing = targetRowIndex !== null && targetRowIndex !== products.length;

    const packageSize = parsePackingQuantity(
      product.Package ?? product.package ?? 1,
      1
    );
    const gstRateValue = parseFloat(
      product.TaxCode ?? product.taxCode ?? 0
    );
    const rateValue = product.Rate ?? 0;
    const mrpValue = product.MRP*product.Package ?? 0;

    const newProduct = {
      id: product.id,
      product: { value: product.id, label: product.Item_Name ?? product.ItemName ?? '' },
      name: product.Item_Name ?? product.ItemName ?? '',
      packing: product.Package ?? product.package ?? '',
      packageSize,
      unit: product.Unit ?? product.unit ?? '',
      gstRate: gstRateValue,
      taxCode: product.TaxCode ?? product.taxCode ?? '',
      rate: rateValue,
      mrp: mrpValue,
      hsnCode: product.HSNCode ?? product.hsnCode ?? product.HSN_Code ?? product.hsn_Code ?? '',
      batch: isEditing ? (products[targetRowIndex]?.batch || '') : '',
      expiry: isEditing ? (products[targetRowIndex]?.expiry || '') : '',
      expiryDisplay: isEditing ? (products[targetRowIndex]?.expiryDisplay || '') : '',
      qty: isEditing ? (products[targetRowIndex]?.qty || 1) : 1,
      foc: isEditing ? (products[targetRowIndex]?.foc || 0) : 0,
      discountAmount: isEditing ? (products[targetRowIndex]?.discountAmount || '') : '',
      discountPercentage: isEditing ? (products[targetRowIndex]?.discountPercentage || '') : '',
    };

    if (isEditing) {
      // Update existing row
      updateProductRow(targetRowIndex, () => newProduct);

      // Focus batch field of the updated row
      setTimeout(() => {
        if (packingRefs.current[targetRowIndex]) {
          packingRefs.current[targetRowIndex].focus();
          packingRefs.current[targetRowIndex].select();
        }
      }, 100);
    } else {
      // Check if there are any blank rows (rows without product) and replace the first one
      const blankRowIndex = products.findIndex(p => !p.id && !p.name);

      if (blankRowIndex !== -1) {
        // Replace the blank row with the new product
        const summaryDiscountPct = parseFloat(summaryDiscountPercentage) || 0;
        const newProducts = [...products];
        newProducts[blankRowIndex] = recalculateProductRow(newProduct, summaryDiscountPct);
        setProducts(newProducts);

        // Focus batch field of the replaced row
        setTimeout(() => {
          if (packingRefs.current[blankRowIndex]) {
            packingRefs.current[blankRowIndex].focus();
            packingRefs.current[blankRowIndex].select();
          }
        }, 100);
      } else {
        // No blank rows, add to the end
        const summaryDiscountPct = parseFloat(summaryDiscountPercentage) || 0;
        setProducts([...products, recalculateProductRow(newProduct, summaryDiscountPct)]);

        // Expand refs arrays
        setTimeout(() => {
          const newLength = products.length + 1;
          if (packingRefs.current.length < newLength) {
            packingRefs.current = [...batchRefs.current, null];
          }
          if (expiryRefs.current.length < newLength) {
            expiryRefs.current = [...expiryRefs.current, null];
          }
          if (packingRefs.current.length < newLength) {
            packingRefs.current = [...packingRefs.current, null];
          }
          if (mrpRefs.current.length < newLength) {
            mrpRefs.current = [...mrpRefs.current, null];
          }
          if (qtyRefs.current.length < newLength) {
            qtyRefs.current = [...qtyRefs.current, null];
          }
          if (focRefs.current.length < newLength) {
            focRefs.current = [...focRefs.current, null];
          }
          if (rateRefs.current.length < newLength) {
            rateRefs.current = [...rateRefs.current, null];
          }
          if (discountRefs.current.length < newLength) {
            discountRefs.current = [...discountRefs.current, null];
          }
          if (discountPercentageRefs.current.length < newLength) {
            discountPercentageRefs.current = [...discountPercentageRefs.current, null];
          }
          if (gstRateRefs.current.length < newLength) {
            gstRateRefs.current = [...gstRateRefs.current, null];
          }
          if (hsnCodeRefs.current.length < newLength) {
            hsnCodeRefs.current = [...hsnCodeRefs.current, null];
          }

          // Focus batch field of the newly added row
          const newIndex = products.length;
          if (packingRefs.current[newIndex]) {
            packingRefs.current[newIndex].focus();
            packingRefs.current[newIndex].select();
          }
        }, 100);
      }
    }
  };

  // Handle clicking on product name to edit
  const handleProductNameClick = (index, e) => {
    e.stopPropagation(); // Prevent row selection
    setEditingProductRowIndex(index);
    setProductSearchForEdit('');

    // Calculate dropdown position based on clicked cell
    setTimeout(() => {
      if (productNameCellRefs.current[index]) {
        const rect = productNameCellRefs.current[index].getBoundingClientRect();
        setDropdownPosition(calculateDropdownPosition(rect));
      }
    }, 50);
  };

  // Filter products based on search input (now productsMaster is already filtered from API)
  const filteredProducts = useMemo(() => {
    const searchText = editingProductRowIndex !== null ? productSearchForEdit : productSearch;
    if (!searchText || searchText.trim().length === 0) {
      return [];
    }
    // Additional client-side filtering if needed (API already filters, but we can refine)
    return productsMaster.filter((prod) => {
      const name = prod.Item_Name ?? prod.ItemName ?? '';
      return name.toLowerCase().includes(searchText.toLowerCase());
    });
  }, [productsMaster, productSearch, productSearchForEdit, editingProductRowIndex]);

  // Reset selected index when filtered list changes
  useEffect(() => {
    const searchText = editingProductRowIndex !== null ? productSearchForEdit : productSearch;
    if (searchText && searchText.trim().length > 0 && filteredProducts.length > 0) {
      setSelectedProductIndex(0);
    } else {
      setSelectedProductIndex(-1);
    }
    productRowRefs.current = [];
  }, [filteredProducts.length, productSearch, productSearchForEdit, editingProductRowIndex]);

  // Show product list when editing a row
  useEffect(() => {
    if (editingProductRowIndex !== null) {
      setShowProductList(true);
      setSelectedProductIndex(-1);
    }
  }, [editingProductRowIndex]);

  // Handle ESC key globally when editing product
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && editingProductRowIndex !== null) {
        e.preventDefault();
        setShowProductList(false);
        setProductSearch('');
        setProductSearchForEdit('');
        setEditingProductRowIndex(null);
        setSelectedProductIndex(-1);
      }
    };

    if (editingProductRowIndex !== null) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [editingProductRowIndex]);

  // Handle click outside to close product dropdown when editing
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (editingProductRowIndex !== null) {
        if (
          productDropdownRef.current &&
          !productDropdownRef.current.contains(e.target) &&
          productNameCellRefs.current[editingProductRowIndex] &&
          !productNameCellRefs.current[editingProductRowIndex].contains(e.target)
        ) {
          setShowProductList(false);
          setProductSearch('');
          setProductSearchForEdit('');
          setEditingProductRowIndex(null);
          setSelectedProductIndex(-1);
        }
      }
    };

    if (editingProductRowIndex !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [editingProductRowIndex]);

  // Initialize with no selection when product list first shows
  useEffect(() => {
    if (showProductList && editingProductRowIndex === null) {
      const searchText = productSearch;
      if (!searchText || searchText.trim().length === 0) {
        setSelectedProductIndex(-1);
      }
    }
  }, [showProductList, editingProductRowIndex, productSearch]);

  // Scroll selected row into view
  useEffect(() => {
    if (showProductList && selectedProductIndex >= 0 && productRowRefs.current[selectedProductIndex] && productDropdownScrollRef.current) {
      const selectedRow = productRowRefs.current[selectedProductIndex];
      const scrollContainer = productDropdownScrollRef.current;

      const rowTop = selectedRow.offsetTop;
      const rowBottom = rowTop + selectedRow.offsetHeight;
      const containerTop = scrollContainer.scrollTop;
      const containerBottom = containerTop + scrollContainer.clientHeight;

      if (rowTop < containerTop) {
        scrollContainer.scrollTop = rowTop - 10;
      } else if (rowBottom > containerBottom) {
        scrollContainer.scrollTop = rowBottom - scrollContainer.clientHeight + 10;
      }
    }
  }, [selectedProductIndex, showProductList]);

  // Handle keyboard navigation in product dropdown
  const handleProductSearchKeyDown = (e) => {
    if (!showProductList) {
      if (e.key === 'Enter' && filteredProducts.length > 0) {
        setShowProductList(true);
        setSelectedProductIndex(-1);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (selectedProductIndex === -1) {
        setSelectedProductIndex(0);
      } else {
        setSelectedProductIndex((prev) =>
          prev < filteredProducts.length - 1 ? prev + 1 : prev
        );
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (selectedProductIndex === -1) {
        setSelectedProductIndex(filteredProducts.length - 1);
      } else {
        setSelectedProductIndex((prev) => (prev > 0 ? prev - 1 : -1));
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedProductIndex >= 0 && selectedProductIndex < filteredProducts.length) {
        const selectedProduct = filteredProducts[selectedProductIndex];
        if (selectedProduct) {
          const targetIndex = editingProductRowIndex !== null ? editingProductRowIndex : products.length;
          handleSelectProductFromSearch(selectedProduct, targetIndex);
        }
      } else {
        setShowProductList(false);
        setEditingProductRowIndex(null);
        setProductSearchForEdit('');
        setTimeout(() => {
          if (submitbtnRef.current) {
            submitbtnRef.current.focus();
          }
        }, 100);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setShowProductList(false);
      setProductSearch('');
      setProductSearchForEdit('');
      setEditingProductRowIndex(null);
      setSelectedProductIndex(-1);
    }
  };

  const handleDeleteRow = (index) => {
    const newProducts = products.filter((_, i) => i !== index);
    setProducts(newProducts);
  };

  const supplierOptions = useMemo(
    () =>
      (suppliers || []).map((supplier) => {
        const supplierId =
          supplier.SupplierID ??
          supplier.supplierID ??
          supplier.Id ??
          supplier.id ??
          supplier.SupplierId;
        return {
          value: supplierId,
          label: supplier.SupplierName ?? supplier.Name ?? '',
          ...supplier,
        };
      }),
    [suppliers]
  );

  const customSupplierOptionLabel = (option) => {
    const supplierName = option.SupplierName ?? option.Name ?? option.label ?? 'Unknown supplier';

    return (
      <div className="flex items-center space-x-3 py-1">
        <div className="font-semibold text-sm text-black">
          {supplierName}
        </div>
      </div>
    );
  };

  const calculateSubTotal = () => {
    // Subtotal should show amount after all discounts but before tax
    return products.reduce((total, product) => {
      const discountedAmount = parseFloat(product.discountedAmount || 0);
      // Fallback to calculating from amount - discounts if discountedAmount not available
      if (discountedAmount === 0) {
        const amount = parseFloat(product.amount || 0);
        const productDiscount = parseFloat(product.calculatedDiscountAmount || 0);
        const summaryDiscount = parseFloat(product.summaryDiscountAmount || 0);
        return total + amount - productDiscount - summaryDiscount;
      }
      return total + discountedAmount;
    }, 0).toFixed(2);
  };

  const calculateTotalSGST = () => {
    return products.reduce((total, product) => total + parseFloat(product.sgst || 0), 0).toFixed(2);
  };

  const calculateTotalCGST = () => {
    return products.reduce((total, product) => total + parseFloat(product.cgst || 0), 0).toFixed(2);
  };

  const calculateGrandTotal = () => {
    return products.reduce((total, product) => total + parseFloat(product.totalAmount || 0), 0).toFixed(2);
  };

  const roundAmount = (total) => {
    if ((total - Math.floor(total)) < 0.5) {
      return Math.floor(total);
    } else {
      return Math.ceil(total);
    }
  };

  const calculateSummaryDiscount = () => {
    let discount = 0;

    if (summaryDiscountAmount && summaryDiscountAmount.trim() !== '') {
      // Fixed amount discount - applied at summary level
      discount = parseFloat(summaryDiscountAmount) || 0;
    } else if (summaryDiscountPercentage && summaryDiscountPercentage.trim() !== '') {
      // Percentage discount - already applied at product level, sum up from products
      discount = products.reduce((total, product) => {
        return total + parseFloat(product.summaryDiscountAmount || 0);
      }, 0);
    }

    return discount.toFixed(2);
  };

  const calculateFinalGrandTotal = () => {
    const grandTotal = parseFloat(calculateGrandTotal()) || 0;
    let discountedTotal = grandTotal;

    // Only apply fixed amount discount at summary level (percentage is already applied at product level)
    if (summaryDiscountAmount && summaryDiscountAmount.trim() !== '') {
      const discount = parseFloat(summaryDiscountAmount) || 0;
      discountedTotal = grandTotal - discount;
    }

    if (isRounded) {
      return roundAmount(discountedTotal).toFixed(2);
    }

    return discountedTotal.toFixed(2);
  };

  const resetForm = () => {
    if (isEditMode) {
      const closeTabId = tabId || tabContext?.activeTabId;
      if (tabContext?.closeTab && closeTabId) {
        tabContext.closeTab(closeTabId);
      }
      return;
    }

    // Clear all form fields
    setInvoiceNumber('');
    setInvoiceDate(formatDateString(new Date()));
    setEntryDate(formatDateString(new Date()));
    setEntryDateISO(formatDateString(new Date()));
    setSelectedSupplier(null);
    setNote('');
    setPaymentType('debit');
    setDueDate('');

    // Clear products
    setProducts([]);

    // Clear summary discount
    setSummaryDiscountAmount('');
    setSummaryDiscountPercentage('');
    setIsRounded(false);
    setPrintAfterSave(false);

    // Clear product search
    setProductSearch('');
    setShowProductList(false);
    setSelectedProductIndex(-1);
    setEditingProductRowIndex(null);
    setProductSearchForEdit('');
    setSelectedIndex(null);
    clearSupplierProductMappingContext();
    setShowSupplierProductMappingModal(false);

    // Clear products master
    setProductsMaster([]);

    // Fetch fresh masters data (will update purchase number)
    fetchMasters();

    // Focus on supplier field
    setTimeout(() => {
      supplierRef.current?.focus();
    }, 100);
  };

  const handleSave = async () => {
    // Prevent multiple simultaneous save operations
    if (isSavingRef.current) {
      return; // Already saving, ignore this call
    }

    // Set saving flag immediately to prevent concurrent calls
    isSavingRef.current = true;
    setIsButtonDisabled(true);
    setLoading(true);

    // Show confirmation dialog
    const result = await Swal.fire({
      title: isEditMode ? 'Update Purchase?' : 'Save Purchase?',
      text: isEditMode
        ? 'Are you sure you want to update this purchase transaction?'
        : 'Are you sure you want to save this purchase transaction?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#4f46e5',
      cancelButtonColor: '#6b7280',
      confirmButtonText: isEditMode ? 'Yes, update it' : 'Yes, save it',
      cancelButtonText: 'Cancel',
      customClass: {
        confirmButton: 'rounded-lg text-sm font-medium px-5 py-2',
        cancelButton: 'rounded-lg text-sm font-medium px-5 py-2 mr-2',
        popup: 'rounded-xl shadow-xl'
      },
      allowOutsideClick: false, // Prevent closing by clicking outside
      allowEscapeKey: false, // Prevent closing with Escape key during save
    });

    // If user cancels, reset flags and return
    if (!result.isConfirmed) {
      isSavingRef.current = false;
      setIsButtonDisabled(false);
      setLoading(false);
      return;
    }

    if (!invoiceNumber.trim()) {
      toast.error('Invoice Number is required.', { autoClose: 1000 });
      isSavingRef.current = false;
      setLoading(false)
      setIsButtonDisabled(false);
      invoiceNumberRef.current.focus()
      return;
    }

    const parseDecimal = (value) => parseFloat(parseFloat(value).toFixed(3));

    if (!selectedSupplier) {
      toast.error('Please select a supplier.', { autoClose: 1000 });
      isSavingRef.current = false;
      setLoading(false)
      setIsButtonDisabled(false);
      supplierRef.current.focus()
      return;
    }

    const summaryDiscountPct = parseFloat(summaryDiscountPercentage) || 0;
    const recalculatedProducts = products.map(product => recalculateProductRow(product, summaryDiscountPct));
    setProducts(recalculatedProducts);

    const validProducts = recalculatedProducts.filter(product => product.id && product.name);

    if (validProducts.length === 0) {
      toast.error('Please select products.', { autoClose: 1000 });
      isSavingRef.current = false;
      setLoading(false)
      setIsButtonDisabled(false);
      if (productSearchInputRef.current) {
        productSearchInputRef.current.focus();
      }
      return;
    }

    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();

    const stopWithFocus = (toastFn, ref) => {
      toastFn();
      if (ref) {
        ref.focus();
        ref.select?.();
      }
      isSavingRef.current = false;
      setLoading(false);
      setIsButtonDisabled(false);
    };

    for (let i = 0; i < recalculatedProducts.length; i++) {
      const product = recalculatedProducts[i];
      if (!product.id || !product.name) continue;

      if (!(product.batch || '').trim()) {
        stopWithFocus(
          () => toast.error('Please enter batch number for each item.', { autoClose: 1500 }),
          batchRefs.current[i]
        );
        return;
      }

      const expiryDisplay = (product.expiryDisplay || '').trim();
      const parsedExpiry = parseExpiryMonthYear(expiryDisplay, product.expiry);
      let month;
      let year;

      if (parsedExpiry) {
        month = parsedExpiry.month;
        year = parsedExpiry.year;
      }

      const numericValue = expiryDisplay.replace(/\D/g, '');
      const isPartialManualEntry = expiryDisplay
        && !isMmmYyFormattedValue(expiryDisplay)
        && numericValue.length > 0
        && numericValue.length < 4;

      if (isPartialManualEntry) {
        stopWithFocus(
          () => toast.error('Please enter a complete expiry date (e.g. May-27 or MM/YY).', { autoClose: 1500 }),
          expiryRefs.current[i]
        );
        return;
      }

      if (month === undefined || year === undefined) {
        stopWithFocus(
          () => toast.error('Please enter expiry date for each item.', { autoClose: 1500 }),
          expiryRefs.current[i]
        );
        return;
      }

      const isValidMonth = month >= 1 && month <= 12;
      const isPastMonth = year < currentYear || (year === currentYear && month < currentMonth);
      if (!isValidMonth || isPastMonth) {
        stopWithFocus(
          () => toast.error('Expiry must be current month or a future month.', { autoClose: 1500 }),
          expiryRefs.current[i]
        );
        return;
      }
    }

    // Subtotal should be the amount after all discounts but before tax
    const subTotal = validProducts.reduce((total, product) => {
      const discountedAmount = parseFloat(product.discountedAmount || 0);
      if (discountedAmount === 0) {
        // Fallback calculation
        const amount = parseFloat(product.amount || 0);
        const productDiscount = parseFloat(product.calculatedDiscountAmount || 0);
        const summaryDiscount = parseFloat(product.summaryDiscountAmount || 0);
        return total + amount - productDiscount - summaryDiscount;
      }
      return total + discountedAmount;
    }, 0);
    const totalProductDiscount = validProducts.reduce((total, product) => total + parseFloat(product.calculatedDiscountAmount || 0), 0);
    const totalSummaryDiscount = validProducts.reduce((total, product) => total + parseFloat(product.summaryDiscountAmount || 0), 0);
    const totalDiscount = totalProductDiscount + (summaryDiscountPct > 0 ? totalSummaryDiscount : 0);
    const totalTax = validProducts.reduce((total, product) => total + parseFloat(product.taxAmount || 0), 0);
    const grandTotalRaw = validProducts.reduce((total, product) => total + parseFloat(product.totalAmount || 0), 0);

    // Calculate summary discount (for fixed amount, apply at summary level)
    const summaryDiscount = parseFloat(calculateSummaryDiscount()) || 0;
    let discountedTotal = grandTotalRaw;
    if (summaryDiscountAmount && summaryDiscountAmount.trim() !== '') {
      // Fixed amount discount - apply at summary level
      discountedTotal = grandTotalRaw - summaryDiscount;
    } else {
      // Percentage discount - already applied at product level
      discountedTotal = grandTotalRaw;
    }

    // Apply rounding if enabled
    let finalAmount = discountedTotal;
    let roundingAmount = 0;
    if (isRounded) {
      const roundedAmount = roundAmount(discountedTotal);
      roundingAmount = roundedAmount - discountedTotal;
      finalAmount = roundedAmount;
    }

    const netAmount = Number(finalAmount.toFixed(2));
    roundingAmount = Number(roundingAmount.toFixed(2));
    const cashAmountValue = paymentType === 'debit' ? netAmount : 0;

    const supplierId =
      selectedSupplier?.SupplierID ??
      selectedSupplier?.supplierID ??
      selectedSupplier?.value ??
      0;

    const supplierName = (
      selectedSupplier?.SupplierName ??
      selectedSupplier?.Name ??
      selectedSupplier?.label ??
      ''
    ).toString().trim();

    const invoiceDateISO = formatDateString(invoiceDate) || formatDateString(new Date());
    const dueDateISO = (() => {
      const baseDate = new Date(invoiceDateISO);
      if (Number.isNaN(baseDate.getTime())) {
        return invoiceDateISO;
      }
      const creditDays = getSupplierCreditDays(selectedSupplier);
      baseDate.setDate(baseDate.getDate() + creditDays);
      return formatDateString(baseDate) || invoiceDateISO;
    })();
    const purchaseDateISO = formatDateString(entryDateISO) || formatDateString(new Date());

    const purchaseDetails = validProducts.map((product, index) => {
      console.log(product);
      const packageSize = getPackageSize(product);
      const totalUnits = getTotalUnits(product);
      const unitRateRaw = getUnitRate(product);
      const unitMrpValue = getUnitMrp(product);

      // Calculate discount amount and percentage
      let discountAmount = parseDecimal(product.discountAmount || 0) || 0;
      let discountPercentage = parseDecimal(product.discountPercentage || 0) || 0;
      const amount = parseFloat(product.amount) || 0;

      // If discount percentage has value and discount amount is zero/empty, calculate amount from percentage
      if (discountPercentage > 0 && discountAmount === 0) {
        discountAmount = parseDecimal((amount * discountPercentage) / 100);
      }
      // If discount amount has value, use it as-is (no need to calculate percentage)

      return {
        SiNo: index + 1,
        ItemNo: product.id,
        ItemName: product.name,
        BatchCode: product.batch,
        Quantity: parseDecimal(totalUnits),
        Foc: parseDecimal(getFocUnits(product)),
        Unit: product.unit || '',
        PackageSize: parseDecimal(packageSize),
        Packing: parseDecimal(packageSize),
        Price: parseDecimal(unitRateRaw),
        Mrp: parseDecimal(unitMrpValue),
        Total: parseDecimal(product.amount) || 0,
        DiscountAmount: discountAmount,
        DiscountPercentage: discountPercentage,
        RoundingAmount: parseDecimal(0),
        TaxCode: String(product.gstRate || 'A'),
        TaxAmount: parseDecimal(product.taxAmount) || 0,
        NetTotal: parseDecimal(product.totalAmount) || 0,
        HscCode: product.hsnCode || '',
        DepartmentID : localStorage.getItem('department_id') || '',
      };
    });

    const purchaseBatches = validProducts.map((product) => {
      const totalUnits = getTotalUnits(product);
      const effectiveUnitRate = getEffectiveUnitRate(product); // Use effective rate (with FOC) for PurchaseBatches
      const unitMrpValue = getUnitMrp(product);

      return {
        ItemNo: product.id,
        BatchCode: product.batch,
        PurchaseDate: invoiceDateISO ?? purchaseDateISO,
        ExpiryDate: formatDateString(product.expiry),
        Quantity: parseDecimal(totalUnits),
        SalesPrice: parseDecimal(unitMrpValue),
        PurchasePrice: parseDecimal(effectiveUnitRate),
      };
    });

    // For percentage discount, it's already included in totalDiscount via product.summaryDiscountAmount
    // For fixed amount discount, add it separately
    const finalDiscountAmount = summaryDiscountPct > 0
      ? totalDiscount  // Percentage already included in totalDiscount
      : totalDiscount + summaryDiscount; // Fixed amount needs to be added

    const purchaseHeader = {
      SupplierID: supplierId,
      SupplierName: supplierName,
      InvoiceDate: (() => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      })(),
      PurchaseRefNo: invoiceNumber,
      PurchaseDate: invoiceDateISO,
      DueDate: dueDateISO,
      Total: parseDecimal(subTotal),
      TaxAmount: parseDecimal(totalTax),
      DiscountAmount: parseDecimal(finalDiscountAmount),
      NetAmount: parseDecimal(netAmount),
      RoudingAmount: parseDecimal(roundingAmount),
      CashAmount: parseDecimal(cashAmountValue),
      PaymentMode: paymentType,
      Remarks: note,
      SourceDocument: 'Pharmacy'
    };

    const payload = {
      PurchaseHeader: purchaseHeader,
      PurchaseDetails: purchaseDetails,
      PurchaseBatches: purchaseBatches,
    };

    console.log(payload);

    try {

      if (isEditMode && editParams) {

        const response = await axios.post(`${BASE_URL}${POST_PURCHASE_TRANSACTION}`, payload, {
          headers: {
            'XApiKey': process.env.REACT_APP_API_KEY,
            'Authorization': `Bearer ${token}`,
            'InvoiceNo': editParams.invoiceNo,
            'Sequence': editParams.sequence,
            'EditNo': editParams.editNo || 0,
            'DocStatus': 'U',
          },
        });

        if (response.status === 200 || response.status === 201) {
          toast.success('Purchase Updated successfully.', { autoClose: 1000 });
          resetForm();
        } else {
          throw new Error('Failed to save purchase transaction');
        }

      } else {
        const response = await axios.post(`${BASE_URL}${POST_PURCHASE_TRANSACTION}`, payload, {
          headers: {
            'XApiKey': process.env.REACT_APP_API_KEY,
            'Authorization': `Bearer ${token}`,
            'DocStatus': 'O',
          },
        });

        if (response.status === 200 || response.status === 201) {
          toast.success('Purchase saved successfully.', { autoClose: 1000 });
          resetForm();
        } else {
          throw new Error('Failed to save purchase transaction');
        }

      }



    } catch (error) {
      console.error('Failed to save purchase transaction:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.Messages ||
        'An error occurred while saving the purchase.';
      toast.error(errorMessage, { autoClose: 1500 });
    } finally {
      // Always clear the saving flag and reset UI state
      isSavingRef.current = false;
      setLoading(false);
      setIsButtonDisabled(false);
    }
  };

  const handleNewItem = () => {
    setShowModal(true);
  };

  const handleNewSupplier = () => {
    setShowSupplierModal(true);
  };

  const handleItemSaved = () => {
    // Refresh products list after saving new item
    // This will be called by the modal after successful save
    if (productSearch && productSearch.length >= 2) {
      getProducts(productSearch);
    }
  };

  const handleSupplierSaved = () => {
    fetchMasters();
  };

  // Wrapper function for modal's setShowModal that expects to be called without arguments
  const handleSupplierModalClose = () => {
    setShowSupplierModal(false);
  };


  return (
    <div className='flex h-full bg-background'>
      {/* <LeftMenu /> */}
      <ToastContainer
        position="top-center"
        autoClose={1000}
        limit={1}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />

      {loading && !aiExtracting && <Spinner />}
      {aiExtracting && <AiAnalysingLoader />}

      <div className='flex-1 flex h-full flex-col overflow-hidden hide-scrollbar'>

        <div className="bg-white p-3 mb-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold text-black flex items-center">
                {isEditMode ? 'Edit Purchase' : 'Purchase Management'}
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="py-1 px-4 bg-indigo-600 text-white font-medium text-sm rounded-md hover:bg-indigo-700 shadow-sm transition-all"
                onClick={() => {
                  handleNewItem();
                }}
              >
                New Item +
              </button>
              <button
                type="button"
                className="py-1 px-4 bg-indigo-600 text-white font-medium text-sm rounded-md hover:bg-indigo-700 shadow-sm transition-all"
                onClick={() => {
                  handleNewSupplier();
                  // Action will be updated later
                }}
              >
                New Supplier +
              </button>

              <div className="relative">
                <input
                  type="file"
                  ref={pdfFileInputRef}
                  onChange={handlePdfUpload}
                  accept=".pdf,application/pdf"
                  className="hidden"
                />
                <button
                  type="button"
                  disabled={!selectedSupplier}
                  className={`py-1 px-4 text-white font-medium text-sm rounded-md shadow-sm transition-all flex items-center gap-2 ${!selectedSupplier
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-teal-600 hover:bg-teal-700'
                    }`}
                  onClick={() => pdfFileInputRef.current?.click()}
                >
                  <FiUpload className="w-4 h-4" />
                  Upload PDF
                </button>
              </div>

              <div className="relative">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleExcelUpload}
                  accept=".xlsx, .xls"
                  className="hidden"
                />
                <button
                  type="button"
                  disabled={!selectedSupplier || !invoiceNumber}
                  className={`py-1 px-4 text-white font-medium text-sm rounded-md shadow-sm transition-all flex items-center gap-2 ${!selectedSupplier || !invoiceNumber
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700'
                    }`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FiUpload className="w-4 h-4" />
                  Upload Excel
                </button>
              </div>

              <button
                type="button"
                disabled={!pendingExcelUploadContext || unmappedSupplierProducts.length === 0}
                className={`py-1 px-4 text-white font-medium text-sm rounded-md shadow-sm transition-all ${!pendingExcelUploadContext || unmappedSupplierProducts.length === 0
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-700'
                  }`}
                onClick={() => setShowSupplierProductMappingModal(true)}
              >
                Reopen Mapping
              </button>

            </div>
          </div>
        </div>

        <div className="bg-white ml-3 mr-3 rounded-lg shadow-sm p-3">
          <div className='w-full flex p-3 bg-gray-50 rounded-lg border border-gray-200 flex-col gap-2.5'>

            <div className="flex items-center gap-2 pb-2 border-b border-gray-300">
              <h3 className="font-semibold text-black text-sm">Purchase Information</h3>
            </div>
            <div className='w-full'>
              <div className='grid grid-cols-12 gap-2.5'>
                {/* Supplier */}
                <div className='col-span-4'>
                  <label className="block text-xs font-medium text-black mb-1">
                    Supplier <span className="text-red-500">*</span>
                  </label>
                  <Select
                    inputId="supplier-input"
                    value={
                      selectedSupplier
                        ? supplierOptions.find(option => option.value === selectedSupplier.value) || selectedSupplier
                        : null
                    }
                    options={supplierOptions}
                    formatOptionLabel={customSupplierOptionLabel}
                    onChange={(selectedOption) => {
                      setSelectedSupplier(selectedOption);
                    }}
                    ref={supplierRef}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        // Only move to next field if no option is currently highlighted/focused
                        const isMenuOpen = supplierRef.current?.state?.menuIsOpen;
                        const focusedOption = supplierRef.current?.state?.focusedOption;

                        if (!isMenuOpen && !focusedOption) {
                          e.preventDefault();
                          invoiceNumberRef.current?.focus()
                        }
                      }
                    }}
                    isClearable
                    noOptionsMessage={() => "No suppliers found"}
                    styles={customSelectStyles}
                    className="w-full"
                  />
                </div>

                {/* Invoice Number */}
                <div className='col-span-2'>
                  <label className="block text-xs font-medium text-black mb-1">
                    Invoice Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter invoice number"
                    className="w-full p-1.5 pl-2 border border-gray-400 bg-white rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-sm"
                    ref={invoiceNumberRef}
                    onKeyDown={(e) => handleKeyDown(e, invoiceDateRef)}
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value.toUpperCase())}
                  />
                </div>

                {/* Invoice Date */}
                <div className='col-span-2'>
                  <label className="block text-xs font-medium text-black mb-1">
                    Invoice Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    className="w-full p-1.5 pl-2 border border-gray-400 bg-white rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-sm"
                    ref={invoiceDateRef}
                    onKeyDown={handleInvoiceDateKeyDown}
                    value={invoiceDate}
                    onChange={(e) => setInvoiceDate(e.target.value)}
                  />
                </div>

                {/* Entry Date */}
                <div className='col-span-2'>
                  <label className="block text-xs font-medium text-black mb-1">
                    Entry Date
                  </label>
                  <input
                    type="text"
                    className="w-full p-1.5 pl-2 border border-gray-400 bg-gray-100 rounded-lg text-sm"
                    value={entryDate}
                    ref={entryDateRef}
                    onKeyDown={(e) => handleKeyDown(e, dueDateRef)}
                    readOnly
                  />
                </div>

                {/* Payment Type */}
                <div className='col-span-2'>
                  <label className="block text-xs font-medium text-black mb-1">
                    Payment Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="w-full p-1.5 pl-2 border border-gray-400 bg-white rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-sm"
                    ref={paymentTypeRef}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        // Only move to next field if no option is currently highlighted/focused
                        const isMenuOpen = paymentTypeRef.current?.state?.menuIsOpen;
                        const focusedOption = paymentTypeRef.current?.state?.focusedOption;

                        if (!isMenuOpen && !focusedOption) {
                          e.preventDefault();
                          noteRef.current?.focus()
                        }
                      }
                    }}
                    value={paymentType}
                    onChange={(e) => setPaymentType(e.target.value)}
                  >
                    <option value="debit">Cash</option>
                    <option value="credit">Credit</option>
                  </select>
                </div>

                {/* Note */}
                <div className='col-span-12'>
                  <label className="block text-xs font-medium text-black mb-1">
                    Note
                  </label>
                  <input
                    type="text"
                    className="w-full p-1.5 pl-2 border border-gray-400 bg-white rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-sm"
                    ref={noteRef}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        if (productSearchInputRef.current) {
                          productSearchInputRef.current.focus();
                        }
                      }
                    }}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Enter any additional notes..."
                  />
                </div>

              </div>
            </div>
          </div>
        </div>

        <div className="mt-3 flex-1 overflow-hidden flex flex-col ml-3 mr-3">
          <div className="flex justify-between items-center mb-2 pb-1.5 border-b border-gray-300">
            <h3 className="font-semibold text-black text-sm">Medicine Selection</h3>
          </div>

          <div className="always-scrollbar rounded-lg border border-gray-400 flex-1 w-full relative bg-white h-full min-h-0">
            <table className="border-collapse text-xs w-full" style={{ minWidth: '1400px' }}>
                <thead className="bg-gray-200 text-left sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2.5 text-md font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '50px' }}>No.</th>
                    <th className="px-3 py-2.5 text-md font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '330px' }}>Product Name</th>
                    <th className="px-3 py-2.5 text-md font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '100px' }}>Packing</th>
                    <th className="px-3 py-2.5 text-md font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '120px' }}>Batch No</th>
                    <th className="px-3 py-2.5 text-md font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '100px' }}>Expiry</th>
                    
                    <th className="px-3 py-2.5 text-md font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '80px' }}>Qty</th>
                    <th className="px-3 py-2.5 text-md font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '80px' }}>Free</th>
                    <th className="px-3 py-2.5 text-md font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '80px' }}>Rate</th>
                    <th className="px-3 py-2.5 text-md font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '80px' }}>MRP</th>
                    <th className="px-3 py-2.5 text-md font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '100px' }}>Amount</th>
                    <th className="px-3 py-2.5 text-md font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '100px' }}>Discount</th>
                    <th className="px-3 py-2.5 text-md font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '100px' }}>Discount %</th>
                    <th className="px-3 py-2.5 text-md font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '100px' }}>HSN Code</th>
                    <th className="px-3 py-2.5 text-md font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '80px' }}>GST%</th>
                    <th className="px-3 py-2.5 text-md font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '100px' }}>SGST</th>
                    <th className="px-3 py-2.5 text-md font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '100px' }}>CGST</th>
                    <th className="px-3 py-2.5 text-md font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '100px' }}>Total</th>
                    <th className="px-3 py-2.5 text-md font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '80px' }}>Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {/* Regular rows with products */}
                  {products.map((product, index) => (
                    <tr
                      key={index}
                      className={`border-b border-gray-300 ${selectedIndex === index
                        ? "bg-indigo-50"
                        : "hover:bg-gray-50"
                        } transition-colors`}
                      onClick={() => setSelectedIndex(index)}
                    >
                      <td className="px-3 py-2.5 text-sm text-black border border-gray-200 whitespace-nowrap">
                        {index + 1}
                      </td>

                      <td
                        className="px-3 py-1 text-black font-medium border border-gray-200 whitespace-nowrap cursor-pointer hover:bg-indigo-50 transition-colors"
                        onClick={(e) => handleProductNameClick(index, e)}
                        style={{ fontSize: '15px' }}
                        ref={(el) => (productNameCellRefs.current[index] = el)}
                        title="Click to change product"
                      >
                        {product.name || '-'}
                      </td>

                      <td className="px-3 py-1.5 text-xs border border-gray-200">
                        <input
                          type="text"
                          className="w-full px-1 py-1 text-black bg-white focus:ring-1 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-center font-medium"
                          value={product.packing || ''}
                          style={{ fontSize: '15px' }}
                          onChange={(e) => handlePackingChange(index, e.target.value)}
                          onKeyDown={(e) => handlePackingKeyDown(e, index)}
                          ref={(el) => (packingRefs.current[index] = el)}
                          placeholder="10x10"
                          onFocus={(e) => e.target.select()}
                          disabled={!product.id}
                        />
                      </td>

                      <td className="px-3 py-1.5 text-xs border border-gray-200">
                        <input
                          type="text"
                          className="w-full px-1 py-1 text-black bg-white focus:ring-1 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-center font-medium"
                          value={product.batch || ''}
                          style={{ fontSize: '15px' }}
                          onChange={(e) => handleBatchChange(index, e.target.value)}
                          onKeyDown={(e) => handleBatchKeyDown(e, index)}
                          ref={(el) => (batchRefs.current[index] = el)}
                          placeholder="BATCH001"
                          onFocus={(e) => e.target.select()}
                          disabled={!product.id}
                        />
                      </td>

                      <td className="px-3 py-1.5 text-xs border border-gray-200">
                        <input
                          type="text"
                          className="w-full px-1 py-1 text-black bg-white focus:ring-1 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-center font-medium"
                          value={product.expiryDisplay || ''}
                          style={{ fontSize: '15px' }}
                          onChange={(e) => handleExpiryInputChange(index, e.target.value)}
                          onKeyDown={(e) => handleExpiryKeyDown(e, index)}
                          ref={(el) => (expiryRefs.current[index] = el)}
                          placeholder="May-27"
                          maxLength={6}
                          onFocus={(e) => e.target.select()}
                          disabled={!product.id}
                        />
                      </td>

                      

                      <td className="px-3 py-1.5 text-xs border border-gray-200">
                        <input
                          type="text"
                          className="w-full px-1 py-1 text-black bg-white focus:ring-1 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-right font-medium"
                          value={product.qty}
                          style={{ fontSize: '15px' }}
                          onChange={(e) => handleQtyChange(index, e.target.value)}
                          onKeyDown={(e) => handleQtyKeyDown(e, index)}
                          ref={(el) => (qtyRefs.current[index] = el)}
                          placeholder="1"
                          onFocus={(e) => e.target.select()}
                          disabled={!product.id}
                        />
                      </td>

                      <td className="px-3 py-1.5 text-xs border border-gray-200">
                        <input
                          type="text"
                          className="w-full px-1 py-1 text-black bg-white focus:ring-1 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-right font-medium"
                          value={product.foc ?? 0}
                          style={{ fontSize: '15px' }}
                          onChange={(e) => handleFOCChange(index, e.target.value)}
                          onKeyDown={(e) => handleFOCKeyDown(e, index)}
                          ref={(el) => (focRefs.current[index] = el)}
                          placeholder="0"
                          onFocus={(e) => e.target.select?.()}
                          disabled={!product.id}
                        />
                      </td>

                      <td className="px-3 py-1.5 text-xs border border-gray-200">
                        <input
                          type="text"
                          className="w-full px-1 py-1 text-black bg-white focus:ring-1 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-right font-medium"
                          value={product.rate || ''}
                          style={{ fontSize: '15px' }}

                          onChange={(e) => handleRateChange(index, e.target.value)}
                          onKeyDown={(e) => handleRateKeyDown(e, index)}
                          ref={(el) => (rateRefs.current[index] = el)}
                          onFocus={(e) => e.target.select()}
                          disabled={!product.id}
                        />
                      </td>

                      <td className="px-3 py-1.5 text-xs border border-gray-200">
                        <input
                          type="text"
                          className="w-full px-1 py-1 text-black bg-white focus:ring-1 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-right font-medium"
                          value={product.mrp || ''}
                          style={{ fontSize: '15px' }}
                          onChange={(e) => handleMRPChange(index, e.target.value)}
                          onKeyDown={(e) => handleMRPKeyDown(e, index)}
                          ref={(el) => (mrpRefs.current[index] = el)}
                          placeholder="0.00"
                          onFocus={(e) => e.target.select()}
                          disabled={!product.id}
                        />
                      </td>

                      <td className="px-3 py-1.5 text-xs text-black font-medium text-right border border-gray-200 whitespace-nowrap">
                        {product.amount ? `₹${parseFloat(product.amount).toFixed(2)}` : '-'}
                      </td>

                      <td className="px-3 py-1.5 text-xs border border-gray-200">
                        <input
                          type="text"
                          className="w-full px-1 py-1 text-black bg-white focus:ring-1 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-right font-medium disabled:bg-gray-100 disabled:cursor-not-allowed"
                          value={product.discountAmount || ''}
                          style={{ fontSize: '15px' }}
                          onChange={(e) => handleDiscountAmountChange(index, e.target.value)}
                          onKeyDown={(e) => handleDiscountKeyDown(e, index)}
                          ref={(el) => (discountRefs.current[index] = el)}
                          placeholder="Amount"
                          title={
                            parseFloat(product.rate || 0) <= 0
                              ? 'Discount not allowed when rate is 0'
                              : 'Discount amount: 0 to item total (qty × rate)'
                          }
                          onFocus={(e) => e.target.select()}
                          disabled={!product.id || parseFloat(product.rate || 0) <= 0}
                        />
                      </td>
                      <td className="px-3 py-1.5 text-xs border border-gray-200">
                        <input
                          type="text"
                          className="w-full px-1 py-1 text-black bg-white focus:ring-1 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-right font-medium disabled:bg-gray-100 disabled:cursor-not-allowed"
                          value={product.discountPercentage || ''}
                          style={{ fontSize: '15px' }}
                          onChange={(e) => handleDiscountPercentageChange(index, e.target.value)}
                          onKeyDown={(e) => handleDiscountPercentageKeyDown(e, index)}
                          ref={(el) => (discountPercentageRefs.current[index] = el)}
                          placeholder="%"
                          title={
                            parseFloat(product.rate || 0) <= 0
                              ? 'Discount not allowed when rate is 0'
                              : 'Discount percentage: 0 to 100'
                          }
                          onFocus={(e) => e.target.select()}
                          disabled={!product.id || parseFloat(product.rate || 0) <= 0}
                        />
                      </td>

                      <td className="px-3 py-1.5 text-xs border border-gray-200">
                        <input
                          type="text"
                          className="w-full px-1 py-1 text-black bg-white focus:ring-1 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-center font-medium"
                          value={product.hsnCode || ''}
                          style={{ fontSize: '15px' }}
                          onChange={(e) => handleHSNCodeChange(index, e.target.value)}
                          onKeyDown={(e) => handleHSNCodeKeyDown(e, index)}
                          ref={(el) => (hsnCodeRefs.current[index] = el)}
                          placeholder="HSN Code"
                          onFocus={(e) => e.target.select()}
                          disabled={!product.id}
                        />
                      </td>

                      <td className="px-3 py-1.5 text-xs border border-gray-200">
                        <select
                          className="w-full px-1 py-1 border border-gray-400 text-black bg-white focus:ring-1 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-right font-medium"
                          style={{ fontSize: '15px' }}
                          value={
                            product.gstRate === '' || product.gstRate === null
                              ? ''
                              : String(product.gstRate)
                          }
                          onChange={(e) => handleGSTRateChange(index, e.target.value)}
                          onKeyDown={(e) => handleGSTRateKeyDown(e, index)}
                          ref={(el) => (gstRateRefs.current[index] = el)}
                          disabled={!product.id}
                        >
                          <option value="">Select</option>
                          <option value="0">0%</option>
                          <option value="5">5%</option>
                          <option value="12">12%</option>
                          <option value="18">18%</option>
                          <option value="28">28%</option>
                        </select>
                      </td>

                      <td className="px-3 py-1.5 text-xs text-black text-right border border-gray-200 whitespace-nowrap">
                        {product.sgst ? `₹${parseFloat(product.sgst).toFixed(2)}` : '-'}
                      </td>

                      <td className="px-3 py-1.5 text-xs text-black text-right border border-gray-200 whitespace-nowrap">
                        {product.cgst ? `₹${parseFloat(product.cgst).toFixed(2)}` : '-'}
                      </td>

                      <td className="px-3 py-1.5 text-xs text-black font-bold text-right border border-gray-200 whitespace-nowrap">
                        {product.totalAmount ? `₹${parseFloat(product.totalAmount).toFixed(2)}` : '-'}
                      </td>

                      <td className="px-3 py-1.5 text-xs border border-gray-200">
                        <button
                          type="button"
                          className="p-0.5 bg-red-50 text-red-500 rounded hover:bg-red-100 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteRow(index);
                          }}
                          title="Remove product"
                        >
                          <MdDelete size={12} />
                        </button>
                      </td>
                    </tr>
                  ))}

                  {/* New entry row - yellow background */}
                  <tr className="bg-gray-200 relative">
                    <td className="px-3 py-1.5 text-xs text-black border border-gray-200 whitespace-nowrap">
                      {products.length + 1}
                    </td>

                    <td className="px-3 py-1.5 relative border border-gray-200">
                      <div className="relative w-full" ref={productSearchContainerRef}>
                        <input
                          className="w-full px-1 py-2.5 text-black focus:ring-1 focus:ring-yellow-400 text-xs"
                          value={productSearch}
                          style={{ fontSize: '18px' }}
                          onChange={(e) => {
                            const searchValue = e.target.value;
                            setProductSearch(searchValue);
                            setShowProductList(true);

                            // Clear previous debounce timeout
                            if (productSearchDebounceRef.current) {
                              clearTimeout(productSearchDebounceRef.current);
                            }

                            // Call API when user types 2+ characters (like getInvoiceItems) with debounce
                            if (searchValue && searchValue.length >= 2) {
                              productSearchDebounceRef.current = setTimeout(() => {
                                getProducts(searchValue);
                              }, 300); // 300ms debounce
                            } else if (searchValue.length === 0) {
                              // Clear products when search is cleared
                              setProductsMaster([]);
                            }

                            // Only focus first item if user has typed something
                            if (searchValue && searchValue.trim().length > 0) {
                              setSelectedProductIndex(0);
                            } else {
                              setSelectedProductIndex(-1);
                            }
                            // Calculate position for dropdown
                            if (productSearchContainerRef.current) {
                              const rect = productSearchContainerRef.current.getBoundingClientRect();
                              setDropdownPosition(calculateDropdownPosition(rect));
                            }
                          }}
                          onFocus={() => {
                            setShowProductList(true);
                            setSelectedProductIndex(-1);
                            // Calculate position for dropdown
                            if (productSearchContainerRef.current) {
                              const rect = productSearchContainerRef.current.getBoundingClientRect();
                              setDropdownPosition(calculateDropdownPosition(rect));
                            }
                          }}
                          onKeyDown={handleProductSearchKeyDown}
                          onBlur={() => {
                            // Delay to allow click on dropdown items
                            if (editingProductRowIndex === null) {
                              setTimeout(() => setShowProductList(false), 200);
                            }
                          }}
                          ref={productSearchInputRef}
                          placeholder="Search product..."
                        />
                      </div>
                    </td>

                    <td className="px-3 py-1.5 border border-gray-200">
                      <input className="w-full border px-1 py-1.5 text-xs" disabled />
                    </td>
                    <td className="px-3 py-1.5 border border-gray-200">
                      <input className="w-full border px-1 py-1.5 text-xs" disabled />
                    </td>
                    <td className="px-3 py-1.5 border border-gray-200">
                      <input className="w-full border px-1 py-1.5 text-xs" disabled />
                    </td>
                    <td className="px-3 py-1.5 border border-gray-200">
                      <input className="w-full border px-1 py-1.5 text-xs" disabled />
                    </td>
                    <td className="px-3 py-1.5 border border-gray-200">
                      <input className="w-full border px-1 py-1.5 text-xs" disabled />
                    </td>
                    <td className="px-3 py-1.5 border border-gray-200">
                      <input className="w-full border px-1 py-1.5 text-xs" disabled />
                    </td>
                    <td className="px-3 py-1.5 border border-gray-200">
                      <input className="w-full border px-1 py-1.5 text-xs" disabled />
                    </td>
                    <td className="px-3 py-1.5 border border-gray-200">
                      <input className="w-full border px-1 py-1.5 text-xs" disabled />
                    </td>
                    <td className="px-3 py-1.5 border border-gray-200">
                      <input className="w-full border px-1 py-1.5 text-xs" disabled />
                    </td>
                    <td className="px-3 py-1.5 border border-gray-200">
                      <input className="w-full border px-1 py-1.5 text-xs" disabled />
                    </td>
                    <td className="px-3 py-1.5 border border-gray-200">
                      <input className="w-full border px-1 py-1.5 text-xs" disabled />
                    </td>
                    <td className="px-3 py-1.5 border border-gray-200">
                      <input className="w-full border px-1 py-1.5 text-xs" disabled />
                    </td>
                    <td className="px-3 py-1.5 border border-gray-200">
                      <input className="w-full border px-1 py-1.5 text-xs" disabled />
                    </td>
                    <td className="px-3 py-1.5 border border-gray-200">
                      <input className="w-full border px-1 py-1.5 text-xs" disabled />
                    </td>
                    <td className="px-3 py-1.5 border border-gray-200">
                      <input className="w-full border px-1 py-1.5 text-xs" disabled />
                    </td>
                    <td className="px-3 py-1.5 border border-gray-200">
                      <input className="w-full border px-1 py-1.5 text-xs" disabled />
                    </td>
                    <td className="px-3 py-1.5 border border-gray-200">
                      <input className="w-full border px-1 py-1.5 text-xs" disabled />
                    </td>
                    <td className="px-3 py-1.5 border border-gray-200">
                      <input className="w-full border px-1 py-1.5 text-xs" disabled />
                    </td>
                    <td className="px-3 py-1.5 border border-gray-200"></td>
                  </tr>
                </tbody>
              </table>
          </div>
        </div>

        <div className="bg-white ml-3 mr-3 mt-3 mb-2 rounded-lg shadow-sm border border-gray-300">
          <div className="p-3">
            <div className="flex flex-wrap items-center gap-4 text-xs">
              {/* Invoice Number */}
              <div className="flex items-center gap-2">
                <span className="font-medium text-black">Reference No:</span>
                <span className="text-lg font-bold text-black">{purchaseNumber}</span>
              </div>

              {/* Summary Items in Row */}
              <div className="flex items-center gap-2">
                <span className="font-medium text-black">Sub Total:</span>
                <span className="font-semibold text-black">₹{products.reduce((total, product) => total + parseFloat(product.amount || 0), 0).toFixed(2)}</span>
              </div>

              <div className="flex items-center gap-2">
                <span className="font-medium text-red-800">Cash Discount:</span>
                <span className="font-semibold text-red-800">₹{(products.reduce((total, product) => total + parseFloat(product.calculatedDiscountAmount || 0), 0) + parseFloat(calculateSummaryDiscount() || 0)).toFixed(2)}</span>
              </div>

              <div className="flex items-center gap-2">
                <span className="font-medium text-black">Taxable Amount:</span>
                <span className="font-semibold text-black">₹{calculateSubTotal()}</span>
              </div>

              <div className="flex items-center gap-2">
                <span className="font-medium text-black">SGST:</span>
                <span className="font-semibold text-black">₹{calculateTotalSGST()}</span>
              </div>

              <div className="flex items-center gap-2">
                <span className="font-medium text-black">CGST:</span>
                <span className="font-semibold text-black">₹{calculateTotalCGST()}</span>
              </div>

              <div className="flex items-center gap-2 px-3 border-l border-r border-gray-400">
                <span className="font-semibold text-black">Grand Total:</span>
                <span className="font-bold text-base text-black">₹{calculateGrandTotal()}</span>
              </div>

              {/* Discount Section */}
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-black">Discount:</label>
                <input
                  type="text"
                  className="w-20 p-1 border border-gray-400 text-black bg-white rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-xs text-right font-medium"
                  value={summaryDiscountAmount}
                  onChange={(e) => handleSummaryDiscountAmountChange(e.target.value)}
                  placeholder="Amount"
                  title="Discount amount: 0 to invoice total (no negatives)"
                />
                <input
                  type="text"
                  className="w-16 p-1 border border-gray-400 text-black bg-white rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-xs text-right font-medium"
                  value={summaryDiscountPercentage}
                  onChange={(e) => handleSummaryDiscountPercentageChange(e.target.value)}
                  placeholder="%"
                  title="Discount percentage: 0 to 100 (no negatives)"
                />
                {parseFloat(calculateSummaryDiscount()) > 0 && (
                  <span className="font-semibold text-red-600 text-xs">-₹{calculateSummaryDiscount()}</span>
                )}
              </div>

              <div className="flex items-center gap-2 px-3 border-l border-r border-gray-400">
                <span className="font-semibold text-black">Final Total:</span>
                <span className="font-bold text-base text-indigo-600">₹{calculateFinalGrandTotal()}</span>
              </div>


              {/* Options and Buttons */}
              <div className="flex items-center gap-3 ml-auto">
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={printAfterSave}
                    onChange={(e) => setPrintAfterSave(e.target.checked)}
                    className="text-black focus:ring-gray-500 h-3.5 w-3.5"
                  />
                  <span className="ml-1.5 text-xs text-black">Print on save</span>
                </label>
                <button
                  type="button"
                  onClick={() => setIsRounded(!isRounded)}
                  className={`py-1 px-2 text-xs font-medium rounded-md border transition-colors ${isRounded
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-black border-gray-400 hover:border-indigo-500 hover:text-indigo-600'
                    }`}
                >
                  Round Off
                </button>
                <button
                  type="button"
                  className={`py-2 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium text-xs rounded-md shadow-sm transition-all ${isButtonDisabled
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:from-indigo-700 hover:to-purple-700'
                    }`}
                  onClick={handleSave}
                  ref={submitbtnRef}
                  disabled={isButtonDisabled}
                >
                  Submit
                </button>
                <button
                  type="button"
                  className="py-2 px-4 bg-btnblue text-white font-medium text-xs rounded-md hover:bg-indigo-700 shadow-sm transition-all"
                  onClick={resetForm}
                >
                  New +
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>


      {(showProductList || editingProductRowIndex !== null) && createPortal(
        <>
          {/* Backdrop for click outside */}
          {editingProductRowIndex !== null && (
            <div
              className="fixed inset-0 z-[99998] bg-transparent"
              onClick={() => {
                setShowProductList(false);
                setProductSearch('');
                setProductSearchForEdit('');
                setEditingProductRowIndex(null);
                setSelectedProductIndex(-1);
              }}
            />
          )}
          <div
            ref={productDropdownRef}
            className="fixed bg-white shadow-2xl border z-[99999] overflow-hidden flex flex-col"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${Math.max(dropdownPosition.width || 1000, 1000)}px`,
              height: `${dropdownPosition.height || 400}px`,
            }}
            onMouseDown={(e) => e.preventDefault()}
          >
            {/* Search input for editing mode */}
            {editingProductRowIndex !== null && (
              <div className="p-3 border-b border-gray-200 bg-gray-50">
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  placeholder="Search product..."
                  value={productSearchForEdit}
                  onChange={(e) => {
                    const searchValue = e.target.value;
                    setProductSearchForEdit(searchValue);

                    // Clear previous debounce timeout
                    if (productSearchDebounceRef.current) {
                      clearTimeout(productSearchDebounceRef.current);
                    }

                    // Call API when user types 2+ characters (like getInvoiceItems) with debounce
                    if (searchValue && searchValue.length >= 2) {
                      productSearchDebounceRef.current = setTimeout(() => {
                        getProducts(searchValue);
                      }, 300); // 300ms debounce
                    } else if (searchValue.length === 0) {
                      // Clear products when search is cleared
                      setProductsMaster([]);
                    }

                    // Only focus first item if user has typed something
                    if (searchValue && searchValue.trim().length > 0) {
                      setSelectedProductIndex(0);
                    } else {
                      setSelectedProductIndex(-1);
                    }
                  }}
                  onKeyDown={handleProductSearchKeyDown}
                  autoFocus
                />
              </div>
            )}
            {filteredProducts.length > 0 && (
              <div ref={productDropdownScrollRef} className="overflow-auto flex-1">
                <table className="border-collapse text-sm" style={{ minWidth: '100%' }}>
                  <thead className="bg-gray-200 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-2 text-left border border-gray-300 font-medium text-black whitespace-nowrap" style={{ minWidth: '50px' }}>SI</th>
                      <th className="px-4 py-2 text-left border border-gray-300 font-medium text-black whitespace-nowrap" style={{ minWidth: '200px' }}>Name</th>
                      <th className="px-4 py-2 text-left border border-gray-300 font-medium text-black whitespace-nowrap" style={{ minWidth: '150px' }}>Chemical</th>
                      <th className="px-4 py-2 text-left border border-gray-300 font-medium text-black whitespace-nowrap" style={{ minWidth: '150px' }}>Company</th>
                      <th className="px-4 py-2 text-left border border-gray-300 font-medium text-black whitespace-nowrap" style={{ minWidth: '100px' }}>Package</th>
                      <th className="px-4 py-2 text-left border border-gray-300 font-medium text-black whitespace-nowrap" style={{ minWidth: '100px' }}>Rate</th>
                      <th className="px-4 py-2 text-left border border-gray-300 font-medium text-black whitespace-nowrap" style={{ minWidth: '100px' }}>MRP</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {filteredProducts.map((prod, prodIndex) => {
                      const isSelected = prodIndex === selectedProductIndex;
                      return (
                        <tr
                          key={prodIndex}
                          ref={(el) => {
                            if (el) {
                              productRowRefs.current[prodIndex] = el;
                            }
                          }}
                          className={`border-b border-gray-200 cursor-pointer ${isSelected
                            ? 'bg-indigo-200 hover:bg-indigo-300'
                            : 'hover:bg-blue-100'
                            } transition-colors`}
                          style={{
                            backgroundColor: isSelected ? '#c7d2fe' : undefined
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setSelectedProductIndex(prodIndex);
                            const targetIndex = editingProductRowIndex !== null ? editingProductRowIndex : products.length;
                            handleSelectProductFromSearch(prod, targetIndex);
                          }}
                          onMouseEnter={() => setSelectedProductIndex(prodIndex)}
                        >
                          <td className="px-4 py-2 border border-gray-200 text-black whitespace-nowrap">{prodIndex + 1}</td>
                          <td className="px-4 py-2 border border-gray-200 text-black whitespace-nowrap">
                            <div className="font-semibold">{prod.Item_Name ?? prod.ItemName ?? ''}</div>
                            <div className="text-xs text-black">Onhand: {prod.Onhand ?? 0}</div>
                          </td>
                          <td className="px-4 py-2 border border-gray-200 text-black whitespace-nowrap">{prod.ChemicalName ?? prod.ChemicalName ?? '-'}</td>
                          <td className="px-4 py-2 border border-gray-200 text-black whitespace-nowrap">{prod.Company ?? prod.company ?? '-'}</td>
                          <td className="px-4 py-2 border border-gray-200 text-black whitespace-nowrap">{prod.Package ?? prod.package ?? '-'}</td>
                          <td className="px-4 py-2 border border-gray-200 text-black whitespace-nowrap">₹{prod.Rate ?? prod.rate ?? 0}</td>
                          <td className="px-4 py-2 border border-gray-200 text-black whitespace-nowrap">₹{prod.MRP ?? prod.mrp ?? 0}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {filteredProducts.length === 0 && (
              <div className="p-4 text-center text-black">
                <p>No products found</p>
              </div>
            )}
          </div>
        </>,
        document.body
      )}

      {showPaymentModal && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm"
          onClick={handleClosePaymentModal}
        >
          <div
            className="bg-white rounded-lg shadow-2xl w-72 p-5"
            onKeyDown={handlePaymentModalKeyDown}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-black mb-2">
              Select Payment Mode
            </h3>
            <p className="text-xs text-black mb-4">
              Choose how you want to record this purchase invoice.
            </p>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                className={`py-2 px-3 rounded-md border text-sm font-semibold transition-colors ${paymentModalSelection === 'debit'
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-black border-gray-400 hover:border-indigo-500 hover:text-indigo-600'
                  }`}
                ref={paymentModalCashButtonRef}
                onClick={() => handlePaymentModeSelection('debit')}
              >
                Cash
              </button>
              <button
                type="button"
                className={`py-2 px-3 rounded-md border text-sm font-semibold transition-colors ${paymentModalSelection === 'credit'
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-black border-gray-400 hover:border-indigo-500 hover:text-indigo-600'
                  }`}
                ref={paymentModalCreditButtonRef}
                onClick={() => handlePaymentModeSelection('credit')}
              >
                Credit
              </button>
            </div>
            <button
              type="button"
              className="mt-4 w-full py-2 px-3 rounded-md border border-gray-400 text-sm font-medium text-black hover:text-black hover:border-gray-400 transition-colors"
              onClick={handleClosePaymentModal}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showSupplierProductMappingModal && pendingExcelUploadContext && (
        <div className="fixed inset-0 z-[75] flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-2xl w-[1100px] max-w-[95vw] max-h-[85vh] overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-black">Map Unmapped Supplier Products</h3>
              <p className="text-xs text-gray-600 mt-1">
                Mapped rows are loaded already. Map these unmapped items once, save, and remaining rows will auto-load.
                Or tick items with no match and use "Create Items" to add them to item master directly.
              </p>
            </div>

            <div className="p-5 overflow-y-auto max-h-[60vh] space-y-3">
              {unmappedSupplierProducts.length > 0 && (
                <div className="grid grid-cols-[auto_1fr_1fr_auto] gap-3 items-center pb-1">
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-indigo-600 cursor-pointer"
                      checked={
                        unmappedItemsToCreate.length > 0 &&
                        unmappedItemsToCreate.length === unmappedSupplierProducts.length
                      }
                      onChange={toggleAllUnmappedItemsForCreation}
                      disabled={isCreatingUnmappedItems || isSavingSupplierProductMappings || isReloadingUnmappedMatches}
                    />
                    Select all
                  </label>
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-medium text-gray-600">Supplier product name</span>
                    <button
                      type="button"
                      className="text-xs font-medium text-indigo-600 hover:text-indigo-800 underline disabled:text-gray-400 disabled:no-underline"
                      onClick={selectOnlyUnmatchedUnmappedItems}
                      disabled={isCreatingUnmappedItems || isSavingSupplierProductMappings || isReloadingUnmappedMatches}
                      title="Tick only the rows with no internal product selected"
                    >
                      Select unmatched ({unmatchedUnmappedProducts.length})
                    </button>
                  </div>
                  <div className="text-xs font-medium text-gray-600">Internal product</div>
                  <div />
                </div>
              )}
              {unmappedSupplierProducts.map((supplierProductName) => (
                <div key={supplierProductName} className="grid grid-cols-[auto_1fr_1fr_auto] gap-3 items-center">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-indigo-600 cursor-pointer"
                    checked={unmappedItemsToCreate.includes(supplierProductName)}
                    onChange={() => toggleUnmappedItemForCreation(supplierProductName)}
                    disabled={isCreatingUnmappedItems || isSavingSupplierProductMappings || isReloadingUnmappedMatches}
                    title="Tick to create this item in item master"
                  />
                  <div className="text-sm font-medium text-black bg-gray-50 px-3 py-2 rounded border border-gray-200">
                    {supplierProductName}
                  </div>
                  <AsyncSelect
                    value={selectedUnmappedProductMap[supplierProductName] || null}
                    onChange={(selectedOption) => handleUnmappedProductSelection(supplierProductName, selectedOption)}
                    loadOptions={loadUnmappedProductOptions}
                    isClearable
                    isSearchable
                    styles={customSelectStyles}
                    placeholder="Select internal product..."
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    defaultOptions={false}
                    cacheOptions
                    noOptionsMessage={({ inputValue }) =>
                      inputValue && inputValue.length < 2
                        ? 'Type at least 2 characters'
                        : 'No products found'
                    }
                    loadingMessage={() => 'Searching...'}
                  />
                  <button
                    type="button"
                    onClick={() => removeUnmappedSupplierProduct(supplierProductName)}
                    className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-md border border-red-200"
                    title="Remove from mapping list"
                    disabled={isCreatingUnmappedItems}
                  >
                    <MdDelete size={16} />
                  </button>
                </div>
              ))}
              {unmappedSupplierProducts.length === 0 && (
                <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-600">No unmapped products left.</p>
                  <p className="text-xs text-gray-500 mt-1">Upload another file to map more supplier items.</p>
                </div>
              )}
            </div>

            <div className="px-5 py-4 border-t border-gray-200 flex justify-between items-center gap-2">
              <div className="flex gap-2">
                <button
                  type="button"
                  className="py-2 px-4 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:bg-emerald-300"
                  onClick={handleCreateItemsFromUnmapped}
                  disabled={
                    isCreatingUnmappedItems ||
                    isSavingSupplierProductMappings ||
                    isReloadingUnmappedMatches ||
                    unmappedItemsToCreate.length === 0
                  }
                  title="Create ticked items in item master (item code = product name)"
                >
                  {isCreatingUnmappedItems
                    ? 'Creating...'
                    : `Create Items${unmappedItemsToCreate.length > 0 ? ` (${unmappedItemsToCreate.length})` : ''}`}
                </button>
                <button
                  type="button"
                  className="py-2 px-4 rounded-md border border-indigo-400 text-sm font-medium text-indigo-600 hover:bg-indigo-50 disabled:text-gray-400 disabled:border-gray-300 disabled:hover:bg-transparent"
                  onClick={handleReloadUnmappedMappings}
                  disabled={
                    isCreatingUnmappedItems ||
                    isSavingSupplierProductMappings ||
                    isReloadingUnmappedMatches ||
                    unmappedSupplierProducts.length === 0
                  }
                  title="Re-pull item master and auto-select any rows that now have a match"
                >
                  {isReloadingUnmappedMatches ? 'Reloading...' : 'Reload Mapping'}
                </button>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="py-2 px-4 rounded-md border border-gray-400 text-sm font-medium text-black hover:border-gray-500"
                  onClick={() => handleCloseSupplierProductMappingModal({ clearContext: false })}
                  disabled={isSavingSupplierProductMappings || isCreatingUnmappedItems || isReloadingUnmappedMatches}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="py-2 px-4 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:bg-indigo-300"
                  onClick={handleSaveSupplierProductMappings}
                  disabled={
                    isSavingSupplierProductMappings ||
                    isCreatingUnmappedItems ||
                    isReloadingUnmappedMatches ||
                    unmappedSupplierProducts.length === 0
                  }
                >
                  {isSavingSupplierProductMappings ? 'Saving...' : 'Save Mapping & Load'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AddItemModal
        showModal={showModal}
        setShowModal={setShowModal}
        onSave={handleItemSaved}
        productToEdit={null}
      />

      {showSupplierModal &&
        <AddSupplierModal
          showModal={showSupplierModal}
          setShowModal={handleSupplierModalClose}
          onSave={handleSupplierSaved}
          supplierToEdit={null}
          mode="Supplier"
          setSelectedItem={setSelectedSupplier}
        />
      }


    </div>

  );
};

const Purchase = () => <PurchaseEntryForm mode="create" />;

export { PurchaseEntryForm };
export default withAuth(Purchase);
